/**
 * custom-fields.js
 * -----------------
 * This file:
 * 1. Reads data from Liquid hidden divs
 * 2. Renders form fields on the product page (text, dropdown, radio, color_swatch)
 * 3. Handles conditional fields (e.g. show "Backside Text" only when "Yes" is selected)
 * 4. Validates required fields and max character limits
 * 5. Sends customer data to cart as "line item properties"
 *
 * Data comes from metaobjects configured in the Shopify admin — this file is just
 * the engine that reads that data and builds the form.
 */

(function () {
  "use strict";

  // NOTE: FormData constructor patch is in custom-fields.liquid (inline script)
  // so it runs BEFORE theme JS loads.

  var _brands = [];
  var _brandLabel = "Car";
  var _comboSets = [];
  var _injectedComboSlide = false;

  // ─── PACK CARDS state (Buy 1 / Buy 2 / Buy 3 multi-set purchase) ────
  var _packEnabled = false;
  var _packCount = 1;
  var _packBasePriceCents = 0;
  var _packFields = [];
  var _packConfigs = [1, 2, 3];

  // ─── 1. READ DATA (from Liquid hidden divs) ──────────────────────────────

  function readFields(scope) {
    scope = scope || document;
    const divs = scope.querySelectorAll(".cf-field-data");
    const fields = [];

    divs.forEach((div) => {
      const rawOptions = div.dataset.options || "";
      const options = rawOptions
        .replace(/<br\s*\/?>/gi, "\n")
        .split("\n")
        .map((o) => o.trim())
        .filter((o) => o.length > 0);

      fields.push({
        label: div.dataset.label || "",
        type: div.dataset.type || "text",
        required: div.dataset.required === "true",
        placeholder: div.dataset.placeholder || "",
        max: parseInt(div.dataset.max) || 0,
        allowEmoji: div.dataset.emoji === "true",
        helper: div.dataset.helper || "",
        description: div.dataset.description || "",
        options: options,
        dependsOn: div.dataset.dependsOn || "",
        showWhen: div.dataset.showWhen || "",
        order: parseInt(div.dataset.order) || 99,
        // Convert label to a snake_case ID: "Select Gender" → "select_gender"
        id: (div.dataset.label || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_|_$/g, ""),
      });
    });

    return fields;
  }

  /** Read car brand divs (keychain products only). */
  function readBrands(scope) {
    scope = scope || document;
    const divs = scope.querySelectorAll(".cf-brand-data");
    const brands = [];
    divs.forEach((div) => {
      const modelDivs = div.querySelectorAll(".cf-model-data");
      const models = [];
      modelDivs.forEach((md) => {
        models.push({
          name: md.dataset.modelName || "",
          image: md.dataset.modelImage || "",
        });
      });
      brands.push({
        name: div.dataset.brand || "",
        models,
        image: div.dataset.image || "",
      });
    });
    return brands;
  }

  // ─── 2. RENDER HTML ──────────────────────────────────────────────────────

  function renderField(field) {
    if (field.type === "note") {
      const note = document.createElement("p");
      note.className = "cf-note";
      note.textContent = field.label;
      return note;
    }

    const wrapper = document.createElement("div");
    wrapper.className = "cf-field-wrapper";
    wrapper.dataset.fieldId = field.id;

    if (field.dependsOn) {
      wrapper.style.display = "none";
      wrapper.dataset.dependsOn = field.dependsOn;
      wrapper.dataset.showWhen = field.showWhen;
    }

    const label = document.createElement("label");
    label.className = "cf-label";
    label.htmlFor = "cf_" + field.id;

    if (field.type === "dropdown") {
      label.textContent = field.label + ": ";
      const valueSpan = document.createElement("span");
      valueSpan.className = "cf-label-value";
      valueSpan.textContent = "";
      label.appendChild(valueSpan);
    } else {
      label.textContent = field.label;
    }

    if (field.required) {
      const star = document.createElement("span");
      star.className = "cf-required-star";
      star.textContent = " *";
      label.appendChild(star);
    }

    const labelRow = document.createElement("div");
    labelRow.className = "cf-label-row";
    labelRow.appendChild(label);
    if (field.helper) {
      const helperSpan = document.createElement("span");
      helperSpan.className = "cf-helper-inline";
      helperSpan.textContent = field.helper;
      labelRow.appendChild(helperSpan);
    }
    wrapper.appendChild(labelRow);

    switch (field.type) {
      case "text": {
        const input = document.createElement("input");
        input.type = "text";
        input.id = "cf_" + field.id;
        input.name = "properties[" + field.label + "]";
        input.className = "cf-input";
        input.placeholder = field.placeholder || "Type here...";
        if (field.required) input.required = true;
        if (field.max > 0) input.maxLength = field.max;

        wrapper.appendChild(input);

        if (field.max > 0) {
          const counter = document.createElement("div");
          counter.className = "cf-char-counter";
          counter.textContent = "0 / " + field.max;
          input.addEventListener("input", () => {
            counter.textContent = input.value.length + " / " + field.max;
          });
          wrapper.appendChild(counter);
        }
        break;
      }

      case "dropdown": {
        const select = document.createElement("select");
        select.id = "cf_" + field.id;
        select.name = "properties[" + field.label + "]";
        select.className = "cf-select";
        if (field.required) select.required = true;

        const defaultOpt = document.createElement("option");
        defaultOpt.value = "";
        defaultOpt.disabled = true;
        defaultOpt.selected = true;
        // Avoid double "Select" prefix if label already starts with "Select"
        defaultOpt.textContent = /^select\b/i.test(field.label)
          ? "-- " + field.label + " --"
          : "-- Select " + field.label + " --";
        select.appendChild(defaultOpt);

        field.options.forEach((opt) => {
          const option = document.createElement("option");
          option.value = opt;
          option.textContent = opt;
          select.appendChild(option);
        });

        const updateLabel = () => {
          const valueSpan = label.querySelector(".cf-label-value");
          if (valueSpan) {
            const selectedOption = select.options[select.selectedIndex];
            if (select.value && selectedOption && selectedOption.value !== "") {
              valueSpan.textContent = selectedOption.textContent;
            } else {
              valueSpan.textContent = "";
            }
          }
        };

        updateLabel();

        select.addEventListener("change", () => {
          updateLabel();
          checkDependencies();
        });

        wrapper.appendChild(select);
        break;
      }

      case "image_dropdown": {
        // Parse options: "Single Rose | https://...jpg"
        const parsedOpts = field.options.map((opt) => {
          const parts = opt.split("|").map((s) => s.trim());
          return { text: parts[0] || opt, image: parts[1] || "" };
        });

        // Hidden input — stores selected value, goes to cart as line item property
        const hiddenInput = document.createElement("input");
        hiddenInput.type = "hidden";
        hiddenInput.id = "cf_" + field.id;
        hiddenInput.name = "properties[" + field.label + "]";
        hiddenInput.value = "";

        // Validation proxy — visible to browser for required check
        const validationInput = document.createElement("input");
        validationInput.type = "text";
        validationInput.className = "cf-img-dd-validation";
        validationInput.tabIndex = -1;
        validationInput.style.cssText =
          "position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;";
        if (field.required) {
          validationInput.required = true;
          validationInput.dataset.cfRequired = "true";
        }

        // Trigger button (shows placeholder or selected text)
        const trigger = document.createElement("div");
        trigger.className = "cf-img-dd-trigger";
        trigger.setAttribute("role", "button");
        trigger.setAttribute("tabindex", "0");

        const triggerText = document.createElement("span");
        triggerText.className = "cf-img-dd-trigger-text";
        triggerText.textContent = field.placeholder || "Please select";

        const triggerArrow = document.createElement("span");
        triggerArrow.className = "cf-img-dd-arrow";

        trigger.appendChild(triggerText);
        trigger.appendChild(triggerArrow);

        // Options list
        const optList = document.createElement("div");
        optList.className = "cf-img-dd-list";
        optList.style.display = "none";

        parsedOpts.forEach((opt) => {
          const item = document.createElement("div");
          item.className = "cf-img-dd-item";
          item.dataset.value = opt.text;

          if (opt.image) {
            const img = document.createElement("img");
            img.src = opt.image;
            img.alt = opt.text;
            img.className = "cf-img-dd-img";
            item.appendChild(img);
          }

          const span = document.createElement("span");
          span.textContent = opt.text;
          item.appendChild(span);

          item.addEventListener("click", () => {
            hiddenInput.value = opt.text;
            validationInput.value = opt.text;
            triggerText.textContent = opt.text;
            trigger.classList.add("cf-img-dd-selected");
            optList.style.display = "none";
            trigger.classList.remove("cf-img-dd-open");
            // Mark selected item
            optList
              .querySelectorAll(".cf-img-dd-item")
              .forEach((el) => el.classList.remove("cf-img-dd-active"));
            item.classList.add("cf-img-dd-active");
            checkDependencies();
          });

          optList.appendChild(item);
        });

        // Toggle open/close
        trigger.addEventListener("click", () => {
          const isOpen = optList.style.display !== "none";
          optList.style.display = isOpen ? "none" : "block";
          trigger.classList.toggle("cf-img-dd-open", !isOpen);
        });

        // Close when clicking outside
        document.addEventListener("click", (e) => {
          if (!wrapper.contains(e.target)) {
            optList.style.display = "none";
            trigger.classList.remove("cf-img-dd-open");
          }
        });

        const ddWrapper = document.createElement("div");
        ddWrapper.className = "cf-img-dd-wrapper";
        ddWrapper.appendChild(trigger);
        ddWrapper.appendChild(optList);

        wrapper.appendChild(hiddenInput);
        wrapper.appendChild(validationInput);
        wrapper.appendChild(ddWrapper);
        break;
      }

      case "radio": {
        const radioGroup = document.createElement("div");
        radioGroup.className = "cf-radio-group";

        field.options.forEach((opt, idx) => {
          const radioWrapper = document.createElement("label");
          radioWrapper.className = "cf-radio-label";

          const radio = document.createElement("input");
          radio.type = "radio";
          radio.name = "properties[" + field.label + "]";
          radio.value = opt;
          radio.className = "cf-radio";
          if (field.required) radio.required = true;
          if (idx === 0) radio.checked = true;

          radio.addEventListener("change", () => checkDependencies());

          radioWrapper.appendChild(radio);
          radioWrapper.appendChild(document.createTextNode(" " + opt));
          radioGroup.appendChild(radioWrapper);
        });

        wrapper.appendChild(radioGroup);
        break;
      }

      case "image": {
        const input = document.createElement("input");
        input.type = "file";
        input.id = "cf_" + field.id;
        input.name = "properties[" + field.label + "]";
        input.accept = "image/*";
        input.className = "cf-file-input";
        if (field.required) input.required = true;

        const preview = document.createElement("div");
        preview.className = "cf-file-preview";
        preview.hidden = true;

        const previewImg = document.createElement("img");
        previewImg.className = "cf-file-preview-img";
        previewImg.alt = "";
        preview.appendChild(previewImg);

        const previewMeta = document.createElement("span");
        previewMeta.className = "cf-file-preview-name";
        preview.appendChild(previewMeta);

        // <span> instead of <button> so theme's button styles don't override
        const removeBtn = document.createElement("span");
        removeBtn.className = "cf-file-remove";
        removeBtn.setAttribute("role", "button");
        removeBtn.setAttribute("tabindex", "0");
        removeBtn.setAttribute("aria-label", "Remove photo");
        removeBtn.innerHTML =
          '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
        preview.appendChild(removeBtn);

        const trigger = document.createElement("label");
        trigger.className = "cf-file-trigger";
        trigger.htmlFor = "cf_" + field.id;
        trigger.textContent = field.placeholder || "Choose photo";

        input.addEventListener("change", () => {
          const file = input.files && input.files[0];
          if (!file) {
            preview.hidden = true;
            trigger.hidden = false;
            return;
          }
          if (previewImg.src) URL.revokeObjectURL(previewImg.src);
          previewImg.src = URL.createObjectURL(file);
          previewMeta.textContent = file.name;
          trigger.hidden = true;
          preview.hidden = false;
        });

        const resetInput = () => {
          input.value = "";
          trigger.hidden = false;
          preview.hidden = true;
          if (previewImg.src) {
            URL.revokeObjectURL(previewImg.src);
            previewImg.removeAttribute("src");
          }
          previewMeta.textContent = "";
        };

        removeBtn.addEventListener("click", resetInput);
        removeBtn.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            resetInput();
          }
        });

        wrapper.appendChild(input);
        wrapper.appendChild(trigger);
        wrapper.appendChild(preview);
        break;
      }

      // Options format: "Label:#hexcode" (e.g. "22k Gold:#D4AF37")
      case "color_swatch": {
        const hiddenInput = document.createElement("input");
        hiddenInput.type = "hidden";
        hiddenInput.id = "cf_" + field.id;
        hiddenInput.name = "properties[" + field.label + "]";
        wrapper.appendChild(hiddenInput);

        const swatchGroup = document.createElement("div");
        swatchGroup.className = "cf-swatch-group";

        field.options.forEach((opt) => {
          const parts = opt.split(":");
          const colorLabel = parts[0].trim();
          const hexCode =
            parts.length > 1
              ? "#" + parts.slice(1).join(":").trim().replace("#", "")
              : "#ccc";

          const swatch = document.createElement("div");
          swatch.className = "cf-swatch";
          swatch.title = colorLabel;
          swatch.style.backgroundColor = hexCode;
          swatch.dataset.value = colorLabel;

          swatch.addEventListener("click", () => {
            swatchGroup
              .querySelectorAll(".cf-swatch")
              .forEach((s) => s.classList.remove("cf-swatch--selected"));
            swatch.classList.add("cf-swatch--selected");
            hiddenInput.value = colorLabel;
          });

          swatchGroup.appendChild(swatch);
        });

        wrapper.appendChild(swatchGroup);
        break;
      }

      default: {
        const input = document.createElement("input");
        input.type = "text";
        input.name = "properties[" + field.label + "]";
        input.className = "cf-input";
        wrapper.appendChild(input);
      }
    }

    if (field.description) {
      const desc = document.createElement("p");
      desc.className = "cf-field-description";
      desc.textContent = field.description;
      wrapper.appendChild(desc);
    }

    return wrapper;
  }

  // ─── 3. CONDITIONAL LOGIC ────────────────────────────────────────────────

  /**
   * Remove required attribute when hiding a field so native browser validation
   * doesn't block submission. Saved in a data attribute to restore later.
   */
  function removeRequiredFromWrapper(wrapper) {
    wrapper.querySelectorAll("input, select, textarea").forEach(function (inp) {
      if (inp.required) {
        inp.dataset.cfRequired = "true";
        inp.removeAttribute("required");
      }
      if (inp.type !== "hidden" && inp.type !== "radio") inp.value = "";
    });
  }

  /** Restore required attribute when showing a previously hidden field. */
  function restoreRequiredInWrapper(wrapper) {
    wrapper
      .querySelectorAll("[data-cf-required='true']")
      .forEach(function (inp) {
        inp.required = true;
      });
  }

  function checkDependencies() {
    const dependentFields = document.querySelectorAll("[data-depends-on]");

    dependentFields.forEach((wrapper) => {
      // Skip fields hidden by combo filter
      if (wrapper.classList.contains("cf-combo-hidden")) return;

      // dependsOn supports pipe-separated parent IDs (OR logic):
      // "field_a | field_b" → show when ANY parent matches showWhen
      const rawDependsOn = wrapper.dataset.dependsOn || "";
      const parentIds = rawDependsOn
        .split("|")
        .map((s) =>
          s
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_|_$/g, ""),
        )
        .filter((s) => s.length > 0);
      const showWhen = wrapper.dataset.showWhen;
      const showWhenList = showWhen
        ? showWhen.split("|").map((v) => v.trim())
        : [];

      let anyParentFound = false;
      let shouldShow = false;

      // Scope parent lookup to the closest .cf-set-group so per-set dependencies
      // work independently. Falls back to document if wrapper isn't in a set.
      const parentScope = wrapper.closest(".cf-set-group") || document;

      for (let i = 0; i < parentIds.length; i++) {
        const parentWrapper = parentScope.querySelector(
          `[data-field-id="${parentIds[i]}"]`,
        );
        if (!parentWrapper) continue;

        anyParentFound = true;
        let val = "";
        const parentInput = parentWrapper.querySelector("select, input");
        if (parentInput) {
          if (parentInput.type === "radio") {
            const checked = parentWrapper.querySelector("input:checked");
            val = checked ? checked.value : "";
          } else {
            val = parentInput.value;
          }
        }

        // OR logic — if any parent matches, show the field
        if (showWhen === "" || showWhenList.indexOf(val) !== -1) {
          shouldShow = true;
          break;
        }
      }

      // No parent in DOM — fail open (field doesn't depend on anything on this product)
      if (!anyParentFound) {
        wrapper.style.display = "block";
        return;
      }

      if (shouldShow) {
        wrapper.style.display = "block";
        restoreRequiredInWrapper(wrapper);
      } else {
        wrapper.style.display = "none";
        removeRequiredFromWrapper(wrapper);
      }
    });
  }

  /**
   * Show/hide custom fields based on the selected combo's allowed field list.
   * allowedIds: null → hide all (no combo selected)
   *             []   → no restriction, show all
   *             ["enter_name","select_color"] → show only these fields
   */
  function applyAllowedFields(allowedIds, scope) {
    scope = scope || document;
    const wrappers = scope.querySelectorAll(".cf-field-wrapper[data-field-id]");
    const hideAll = allowedIds === null;

    wrappers.forEach((wrapper) => {
      const fieldId = wrapper.dataset.fieldId;
      // Skip combo/brand/model dropdowns — they are not subject to this filter
      if (
        fieldId === "combo_set" ||
        fieldId === "car_brand" ||
        fieldId === "car_model"
      )
        return;

      if (
        !hideAll &&
        (allowedIds.length === 0 || allowedIds.indexOf(fieldId) !== -1)
      ) {
        wrapper.classList.remove("cf-combo-hidden");
        if (!wrapper.dataset.dependsOn) {
          wrapper.style.display = "block";
          restoreRequiredInWrapper(wrapper);
        }
      } else {
        wrapper.classList.add("cf-combo-hidden");
        wrapper.style.display = "none";
        removeRequiredFromWrapper(wrapper);
      }
    });

    // Pack cards mode: hide empty set groups so no orphan headings show
    if (typeof updateSetVisibility === "function") updateSetVisibility();
  }

  // ─── 4. CAR BRAND DROPDOWN (keychain products) ──────────────────────────

  function renderCarBrands(brands, targetContainer, cfScope) {
    if (brands.length === 0) return;

    const cfContainer =
      cfScope || document.getElementById("custom-fields-container");
    const rawBl =
      cfContainer && cfContainer.dataset.brandLabel
        ? cfContainer.dataset.brandLabel
        : "Car";
    const bl = rawBl.charAt(0).toUpperCase() + rawBl.slice(1).toLowerCase();

    const container =
      targetContainer || document.getElementById("cf-render-area");

    const brandWrapper = document.createElement("div");
    brandWrapper.className = "cf-field-wrapper";
    brandWrapper.dataset.fieldId = "car_brand";

    const brandLabel = document.createElement("label");
    brandLabel.textContent = "Select " + bl + " Brand Name *";
    brandLabel.className = "cf-label";
    brandWrapper.appendChild(brandLabel);

    const brandSelect = document.createElement("select");
    brandSelect.className = "cf-select";
    brandSelect.name = "properties[" + bl + " Brand]";
    brandSelect.required = true;
    brandSelect.id = "cf_car_brand";

    const defaultOpt = document.createElement("option");
    defaultOpt.value = "";
    defaultOpt.disabled = true;
    defaultOpt.selected = true;
    defaultOpt.textContent = "-- Select Brand --";
    brandSelect.appendChild(defaultOpt);

    brands.forEach((brand) => {
      const opt = document.createElement("option");
      opt.value = brand.name;
      opt.textContent = brand.name;
      if (brand.image) opt.dataset.image = brand.image;
      brandSelect.appendChild(opt);
    });

    brandWrapper.appendChild(brandSelect);
    container.appendChild(brandWrapper);

    // Persistent MutationObserver: strip theme select plugin wrappers immediately
    // whenever the plugin tries to wrap our native <select>.
    (function () {
      var _cfStripping = false;
      function stripBrandPlugin() {
        var wrap = brandWrapper.querySelector(".select-wrapper");
        if (!wrap || _cfStripping) return;
        _cfStripping = true;
        brandSelect.classList.remove(
          "hidden",
          "semantic-select-initialized",
          "select-init",
          "changed",
        );
        brandWrapper.insertBefore(brandSelect, wrap);
        wrap.remove();
        setTimeout(function () {
          _cfStripping = false;
        }, 100);
      }
      var obs = new MutationObserver(stripBrandPlugin);
      obs.observe(brandWrapper, { childList: true, subtree: true });
    })();

    const modelWrapper = document.createElement("div");
    modelWrapper.className = "cf-field-wrapper";
    modelWrapper.dataset.fieldId = "car_model";
    modelWrapper.style.display = "none";

    const modelLabel = document.createElement("label");
    modelLabel.className = "cf-label";
    modelWrapper.appendChild(modelLabel);

    const modelSelect = document.createElement("select");
    modelSelect.className = "cf-select";
    modelSelect.name = "properties[" + bl + " Model]";
    modelSelect.required = true;
    modelSelect.id = "cf_car_model";
    modelWrapper.appendChild(modelSelect);

    const productPreview = document.createElement("div");
    productPreview.className = "cf-car-product-preview";
    productPreview.style.display = "none";

    // Insert preview ABOVE brand dropdown, then brand + model below it
    container.insertBefore(productPreview, brandWrapper);
    container.appendChild(modelWrapper);

    // setTimeout(0): some themes fire the change event before setting the value,
    // so we read it on the next tick.
    brandSelect.addEventListener("change", () => {
      productPreview.style.display = "none";
      productPreview.innerHTML = "";
      setTimeout(() => {
        const selectedBrand = brands.find((b) => b.name === brandSelect.value);
        modelSelect.innerHTML = '<option value="">-- Select Model --</option>';

        if (selectedBrand) {
          modelLabel.textContent =
            "Select Your " + selectedBrand.name + " Model *";

          selectedBrand.models.forEach((model) => {
            const opt = document.createElement("option");
            opt.value = model.name;
            opt.textContent = model.name;
            modelSelect.appendChild(opt);
          });
          modelWrapper.style.display = "block";
        } else {
          modelWrapper.style.display = "none";
        }
        checkDependencies();
      }, 0);
    });

    modelSelect.addEventListener("change", () => {
      checkDependencies();
      const selectedBrand = brands.find((b) => b.name === brandSelect.value);
      const selectedModel = selectedBrand
        ? selectedBrand.models.find((m) => m.name === modelSelect.value)
        : null;
      if (!selectedBrand || !selectedModel) {
        productPreview.style.display = "none";
        return;
      }

      productPreview.innerHTML = "";
      const card = document.createElement("div");
      card.className = "cf-car-preview-card";

      if (selectedModel.image) {
        const img = document.createElement("img");
        img.src = selectedModel.image;
        img.alt = selectedBrand.name + " - " + selectedModel.name;
        img.className = "cf-car-preview-img";

        // Zoom tooltip — appended to body (not inside card) to avoid overflow clipping
        const zoom = document.createElement("div");
        zoom.className = "cf-car-preview-zoom";
        const zoomImg = document.createElement("img");
        zoomImg.src = selectedModel.image;
        zoomImg.alt = img.alt;
        zoomImg.className = "cf-car-preview-zoom-img";
        zoom.appendChild(zoomImg);
        document.body.appendChild(zoom);

        img.addEventListener("mouseenter", function () {
          var rect = img.getBoundingClientRect();
          zoom.style.display = "block";
          var left = rect.left + rect.width / 2 - 100 + window.scrollX;
          var top = rect.top - 210 + window.scrollY;
          if (left < 8) left = 8;
          if (left + 200 > window.innerWidth - 8)
            left = window.innerWidth - 208;
          if (top < window.scrollY + 8) top = rect.bottom + 8 + window.scrollY;
          zoom.style.left = left + "px";
          zoom.style.top = top + "px";
        });
        img.addEventListener("mouseleave", function () {
          zoom.style.display = "none";
        });

        // Mobile: tap to toggle
        img.addEventListener("click", function (e) {
          e.stopPropagation();
          if (zoom.style.display === "block") {
            zoom.style.display = "none";
          } else {
            var rect = img.getBoundingClientRect();
            var left = rect.left + rect.width / 2 - 100 + window.scrollX;
            var top = rect.top - 210 + window.scrollY;
            if (left < 8) left = 8;
            if (left + 200 > window.innerWidth - 8)
              left = window.innerWidth - 208;
            if (top < window.scrollY + 8)
              top = rect.bottom + 8 + window.scrollY;
            zoom.style.left = left + "px";
            zoom.style.top = top + "px";
            zoom.style.display = "block";
          }
        });
        document.addEventListener("click", function () {
          zoom.style.display = "none";
        });

        card.appendChild(img);
      }

      const textWrap = document.createElement("div");
      textWrap.className = "cf-car-preview-text";

      const name = document.createElement("div");
      name.className = "cf-car-preview-name";
      name.textContent = selectedBrand.name + " - " + modelSelect.value;
      textWrap.appendChild(name);

      const priceStr = (function () {
        try {
          var pd = JSON.parse(
            (document.getElementById("pd-data") || {}).textContent || "{}",
          );
          if (pd.price) return "\u20B9\u00A0" + (pd.price / 100).toFixed(2);
        } catch (e) {}
        return "";
      })();
      if (priceStr) {
        const priceEl = document.createElement("div");
        priceEl.className = "cf-car-preview-price";
        priceEl.textContent = priceStr;
        textWrap.appendChild(priceEl);
      }

      card.appendChild(textWrap);

      const check = document.createElement("div");
      check.className = "cf-car-preview-check";
      check.innerHTML = "&#10003;";
      card.appendChild(check);

      productPreview.appendChild(card);
      productPreview.style.display = "block";
    });
  }

  // ─── 4B. COMBO SET GRID (hamper products) ───────────────────────────────

  function readComboSets(scope) {
    scope = scope || document;
    const divs = scope.querySelectorAll(".cf-combo-data");
    const combos = [];
    divs.forEach((div) => {
      const rawAllowed = div.dataset.allowedFields || "";
      const allowedIds = rawAllowed
        .replace(/<br\s*\/?>/gi, "\n")
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0)
        .map((label) =>
          label
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_|_$/g, ""),
        );

      const combo = {
        name: div.dataset.comboName || "",
        desc: div.dataset.comboDesc || "",
        allowedIds: allowedIds,
        comboImage: div.dataset.comboImage || "",
        items: [],
      };

      const itemDivs = div.querySelectorAll(".cf-combo-item-data");
      itemDivs.forEach((itemDiv) => {
        combo.items.push({
          name: itemDiv.dataset.itemName || "",
          image: itemDiv.dataset.itemImage || "",
          note: itemDiv.dataset.itemNote || "",
        });
      });

      combos.push(combo);
    });
    return combos;
  }

  function switchProductImage(imageUrl) {
    if (!imageUrl) return;

    var swiperEl = document.querySelector("ul.l4pr.s4wi .swiper-initialized");
    if (!swiperEl || !swiperEl.swiper) return;
    var swiper = swiperEl.swiper;

    // Remove previously injected combo slide
    if (_injectedComboSlide) {
      swiper.removeSlide(0);
      _injectedComboSlide = false;
    }

    // Extract filename from URL (ignore query params)
    var comboFilename = imageUrl.split("?")[0].split("/").pop();

    // Find existing slide with matching image filename
    var targetIndex = -1;
    swiper.slides.forEach(function (slide, i) {
      if (slide.classList.contains("swiper-slide-duplicate")) return;
      var img = slide.querySelector("img");
      if (img && img.src) {
        var slideFilename = img.src.split("?")[0].split("/").pop();
        if (slideFilename === comboFilename) targetIndex = i;
      }
      // Also check srcset / data-src for lazy-loaded images
      if (targetIndex === -1 && img) {
        var lazySrc = img.dataset.src || img.dataset.lazySrc || "";
        if (lazySrc) {
          var lazyFilename = lazySrc.split("?")[0].split("/").pop();
          if (lazyFilename === comboFilename) targetIndex = i;
        }
      }
    });

    if (targetIndex >= 0) {
      swiper.slideTo(targetIndex);
    } else {
      // Image not in swiper — inject as first slide temporarily
      swiper.prependSlide(
        '<div class="swiper-slide"><img src="' +
          imageUrl +
          '" style="width:100%;height:100%;object-fit:cover;display:block;"></div>',
      );
      _injectedComboSlide = true;
      swiper.slideTo(0);
    }
  }

  function renderComboSets(comboSets, targetContainer) {
    if (comboSets.length === 0) return;

    const container =
      targetContainer || document.getElementById("cf-render-area");

    const previewArea = document.createElement("div");
    previewArea.className = "cf-combo-preview";
    previewArea.style.display = "none";

    function showComboItems(combo) {
      previewArea.innerHTML = "";
      if (!combo || combo.items.length === 0) {
        previewArea.style.display = "none";
        return;
      }

      const offerEl = document.getElementById("cf-offer-text");
      if (offerEl && offerEl.innerHTML.trim()) {
        const complimentary = document.createElement("div");
        complimentary.className = "cf-combo-complimentary";
        complimentary.innerHTML = offerEl.innerHTML;
        previewArea.appendChild(complimentary);
      }

      const heading = document.createElement("div");
      heading.className = "cf-combo-preview-heading";
      heading.textContent = "Selected Items";
      previewArea.appendChild(heading);

      const row = document.createElement("div");
      row.className = "cf-combo-horizontal";

      combo.items.forEach((item) => {
        const card = document.createElement("div");
        card.className = "cf-combo-hcard";

        if (item.image) {
          const imgWrap = document.createElement("div");
          imgWrap.className = "cf-combo-hcard-img-wrap";
          const img = document.createElement("img");
          img.src = item.image;
          img.alt = item.name;
          img.className = "cf-combo-hcard-img";
          imgWrap.appendChild(img);
          card.appendChild(imgWrap);
        }

        const textWrap = document.createElement("div");
        textWrap.className = "cf-combo-hcard-text";
        const name = document.createElement("div");
        name.className = "cf-combo-hcard-name";
        name.textContent = item.name;
        textWrap.appendChild(name);
        card.appendChild(textWrap);

        const checkBadge = document.createElement("div");
        checkBadge.className = "cf-combo-hcard-check";
        checkBadge.innerHTML = "&#10003;";
        card.appendChild(checkBadge);

        row.appendChild(card);
      });

      previewArea.appendChild(row);
      previewArea.style.display = "block";
    }

    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = "properties[Combo Set]";

    if (comboSets.length === 1) {
      hiddenInput.value = comboSets[0].name;
      container.appendChild(hiddenInput);
      showComboItems(comboSets[0]);
      container.appendChild(previewArea);
      setTimeout(() => {
        applyAllowedFields(comboSets[0].allowedIds);
        checkDependencies();
      }, 0);
    } else {
      const selectWrapper = document.createElement("div");
      selectWrapper.className = "cf-field-wrapper";
      selectWrapper.dataset.fieldId = "combo_set";

      const label = document.createElement("label");
      label.className = "cf-label";
      label.textContent = "Gift Set Combo";
      const star = document.createElement("span");
      star.className = "cf-required-star";
      star.textContent = " *";
      label.appendChild(star);
      selectWrapper.appendChild(label);

      const select = document.createElement("select");
      select.className = "cf-select";
      select.name = "properties[Combo Set]";
      select.required = true;

      comboSets.forEach((combo) => {
        const opt = document.createElement("option");
        opt.value = combo.name;
        opt.textContent = combo.name + (combo.desc ? " — " + combo.desc : "");
        select.appendChild(opt);
      });

      // Auto-select first combo on load
      select.value = comboSets[0].name;
      showComboItems(comboSets[0]);
      if (comboSets[0].comboImage) switchProductImage(comboSets[0].comboImage);

      select.addEventListener("change", () => {
        const selectedCombo = comboSets.find((c) => c.name === select.value);
        showComboItems(selectedCombo);
        applyAllowedFields(selectedCombo ? selectedCombo.allowedIds : null);
        checkDependencies();
        if (selectedCombo && selectedCombo.comboImage)
          switchProductImage(selectedCombo.comboImage);
      });

      selectWrapper.appendChild(select);
      container.appendChild(selectWrapper);
      container.appendChild(previewArea);
    }
  }

  // ─── 4C. GREETING CARD ADDON ("Make it More Special") ──────────────────

  function readAddonCards(scope) {
    scope = scope || document;
    var divs = scope.querySelectorAll(".cf-addon-card-data");
    var cards = [];
    divs.forEach(function (div) {
      cards.push({
        name: div.dataset.cardName || "",
        desc: div.dataset.cardDesc || "",
        image: div.dataset.cardImage || "",
        price: parseFloat(div.dataset.cardPrice) || 29,
        variantId: div.dataset.cardVariant || "",
      });
    });
    return cards;
  }

  function readAddonConfig(scope) {
    scope = scope || document;
    var configDiv = scope.querySelector(".cf-addon-config");
    return {
      title: configDiv
        ? configDiv.dataset.addonTitle || "Make it More Special"
        : "Make it More Special",
      subtitle: configDiv ? configDiv.dataset.addonSubtitle || "" : "",
    };
  }

  function renderAddonCards(cards, targetContainer) {
    if (cards.length === 0) return;

    var container =
      targetContainer || document.getElementById("cf-render-area");

    var addonConfig = readAddonConfig(
      container.closest("#custom-fields-container") || document,
    );

    // Track selected variant IDs for cart injection
    var selectedVariantIds = [];

    var section = document.createElement("div");
    section.className = "cf-addon-section";
    section.dataset.addonSection = "true";

    // Accordion header
    var header = document.createElement("div");
    header.className = "cf-addon-header";

    var headerLabel = document.createElement("span");
    headerLabel.className = "cf-label";
    headerLabel.textContent = addonConfig.title;
    header.appendChild(headerLabel);

    var chevron = document.createElement("span");
    chevron.className = "cf-addon-chevron";
    chevron.innerHTML =
      '<svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1l5 5 5-5" stroke="#111" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>';
    header.appendChild(chevron);

    section.appendChild(header);

    // Collapsible body
    var body = document.createElement("div");
    body.className = "cf-addon-body";
    body.style.display = "none";

    if (addonConfig.subtitle) {
      var subtitle = document.createElement("div");
      subtitle.className = "cf-addon-subtitle";
      subtitle.textContent = addonConfig.subtitle;
      body.appendChild(subtitle);
    }

    // Card grid
    var grid = document.createElement("div");
    grid.className = "cf-addon-grid";

    cards.forEach(function (card) {
      var item = document.createElement("div");
      item.className = "cf-addon-card";

      var imgWrap = document.createElement("div");
      imgWrap.className = "cf-addon-card-img-wrap";

      if (card.image) {
        var img = document.createElement("img");
        img.src = card.image;
        img.alt = card.name;
        img.className = "cf-addon-card-img";
        img.loading = "lazy";
        imgWrap.appendChild(img);
      }
      item.appendChild(imgWrap);

      // Tooltip — appended to body to avoid overflow clipping
      var tooltip = document.createElement("div");
      tooltip.className = "cf-addon-tooltip";

      if (card.image) {
        var tipImg = document.createElement("img");
        tipImg.src = card.image;
        tipImg.alt = card.name;
        tipImg.className = "cf-addon-tooltip-img";
        tooltip.appendChild(tipImg);
      }

      var tipText = document.createElement("div");
      tipText.className = "cf-addon-tooltip-text";

      var tooltipName = document.createElement("div");
      tooltipName.className = "cf-addon-tooltip-name";
      tooltipName.textContent = card.name + (card.name ? ":" : "");
      tipText.appendChild(tooltipName);

      if (card.desc) {
        var tooltipDesc = document.createElement("div");
        tooltipDesc.className = "cf-addon-tooltip-desc";
        tooltipDesc.textContent = card.desc;
        tipText.appendChild(tooltipDesc);
      }

      var tooltipPrice = document.createElement("div");
      tooltipPrice.className = "cf-addon-tooltip-price";
      tooltipPrice.textContent = "(\u20B9 " + card.price.toFixed(2) + ")";
      tipText.appendChild(tooltipPrice);

      tooltip.appendChild(tipText);
      document.body.appendChild(tooltip);

      // Position tooltip on hover using getBoundingClientRect
      item.addEventListener("mouseenter", function () {
        var rect = item.getBoundingClientRect();
        tooltip.style.display = "block";
        var tipW = tooltip.offsetWidth;
        var left = rect.left + rect.width / 2 - tipW / 2 + window.scrollX;
        var top = rect.top - tooltip.offsetHeight - 8 + window.scrollY;
        // Keep within viewport
        if (left < 8) left = 8;
        if (left + tipW > window.innerWidth - 8)
          left = window.innerWidth - 8 - tipW;
        tooltip.style.left = left + "px";
        tooltip.style.top = top + "px";
      });
      item.addEventListener("mouseleave", function () {
        tooltip.style.display = "none";
      });

      // Multi-select: click toggles selection
      item.addEventListener("click", function (e) {
        e.stopPropagation();
        e.preventDefault();
        var vid = card.variantId;
        var isSelected = item.dataset.cfSelected === "1";
        console.log("[CF] Card click — isSelected:", isSelected, "vid:", vid);
        if (isSelected) {
          item.dataset.cfSelected = "0";
          item.classList.remove("cf-addon-card--selected");
          if (vid)
            selectedVariantIds = selectedVariantIds.filter(function (v) {
              return v !== vid;
            });
        } else {
          item.dataset.cfSelected = "1";
          item.classList.add("cf-addon-card--selected");
          if (vid) selectedVariantIds.push(vid);
        }
      });

      grid.appendChild(item);
    });

    body.appendChild(grid);
    section.appendChild(body);

    // Accordion — open by default
    var isOpen = true;
    body.style.display = "block";
    header.classList.add("cf-addon-header--open");

    header.addEventListener("click", function () {
      isOpen = !isOpen;
      body.style.display = isOpen ? "block" : "none";
      header.classList.toggle("cf-addon-header--open", isOpen);
    });

    container.appendChild(section);

    // Expose selected variant IDs for cart injection
    section._getSelectedVariantIds = function () {
      return selectedVariantIds.slice();
    };

    // Clear all selections after a successful cart add
    section._clearSelections = function () {
      selectedVariantIds = [];
      grid.querySelectorAll(".cf-addon-card--selected").forEach(function (el) {
        el.classList.remove("cf-addon-card--selected");
        el.dataset.cfSelected = "0";
      });
    };
  }

  /** Returns selected addon card items array for cart injection. */
  function getAddonItems() {
    var section = document.querySelector("[data-addon-section='true']");
    console.log("[CF] getAddonItems — section found:", !!section);
    if (!section || !section._getSelectedVariantIds) return [];
    var ids = section._getSelectedVariantIds();
    console.log("[CF] getAddonItems — selectedVariantIds:", ids);
    return ids.map(function (id) {
      return { id: parseInt(id), quantity: 1 };
    });
  }

  // ─── 4D. VARIANT PICKER SLIDER ──────────────────────────────────────────

  function readVariantPicker(scope) {
    scope = scope || document;
    var config = scope.querySelector(".cf-vp-config");
    if (!config) return null;
    var variants = [];
    scope.querySelectorAll(".cf-vp-variant").forEach(function (div) {
      variants.push({
        id: div.dataset.variantId || "",
        title: div.dataset.variantTitle || "",
        price: parseInt(div.dataset.variantPrice) || 0,
        image: div.dataset.variantImage || "",
      });
    });
    if (!variants.length) return null;
    return {
      title: config.dataset.vpTitle || "",
      description: config.dataset.vpDescription || "",
      variants: variants,
    };
  }

  function renderVariantPicker(data) {
    if (!data) return;
    var container = document.getElementById("cf-render-area");
    if (!container) return;

    var selectedVariantId = null;
    var currentIndex = 0;

    function getVisibleCount() {
      return window.innerWidth >= 760 ? 5.7 : 4.8;
    }
    function getScrollBy() {
      return Math.floor(getVisibleCount());
    }

    var section = document.createElement("div");
    section.className = "cf-vp-section";
    section.dataset.vpSection = "true";

    // Title + price
    var minPrice = data.variants[0].price;
    var titleEl = document.createElement("div");
    titleEl.className = "cf-vp-title";
    titleEl.innerHTML =
      data.title +
      ' <span class="cf-vp-title-price">(+ \u20B9\u00A0' +
      (minPrice / 100).toFixed(2) +
      ")</span>";
    section.appendChild(titleEl);

    // Track wrapper
    var trackWrap = document.createElement("div");
    trackWrap.className = "cf-vp-track-wrap";

    var track = document.createElement("div");
    track.className = "cf-vp-track";

    data.variants.forEach(function (variant) {
      var card = document.createElement("div");
      card.className = "cf-vp-card";

      var imgWrap = document.createElement("div");
      imgWrap.className = "cf-vp-img-wrap";

      if (variant.image) {
        var img = document.createElement("img");
        img.src = variant.image;
        img.alt = variant.title;
        img.className = "cf-vp-img";
        img.loading = "lazy";
        imgWrap.appendChild(img);
      }
      card.appendChild(imgWrap);

      if (variant.title && variant.title !== "Default Title") {
        var label = document.createElement("div");
        label.className = "cf-vp-card-label";
        label.textContent = variant.title;
        card.appendChild(label);
      }

      var price = document.createElement("div");
      price.className = "cf-vp-card-price";
      price.textContent = "\u20B9\u00A0" + (variant.price / 100).toFixed(2);
      card.appendChild(price);

      card.addEventListener("click", function () {
        track.querySelectorAll(".cf-vp-card--selected").forEach(function (c) {
          c.classList.remove("cf-vp-card--selected");
        });
        card.classList.add("cf-vp-card--selected");
        selectedVariantId = variant.id;
      });

      track.appendChild(card);
    });

    trackWrap.appendChild(track);
    section.appendChild(trackWrap);

    // Bottom row — description + arrows
    var bottom = document.createElement("div");
    bottom.className = "cf-vp-bottom";

    var desc = document.createElement("div");
    desc.className = "cf-vp-description";
    desc.textContent = data.description;
    bottom.appendChild(desc);

    var arrows = document.createElement("div");
    arrows.className = "cf-vp-arrows";

    var prevBtn = document.createElement("button");
    prevBtn.type = "button";
    prevBtn.className = "cf-vp-arrow";
    prevBtn.innerHTML = "&#8249;";
    prevBtn.disabled = true;

    var nextBtn = document.createElement("button");
    nextBtn.type = "button";
    nextBtn.className = "cf-vp-arrow";
    nextBtn.innerHTML = "&#8250;";

    arrows.appendChild(prevBtn);
    arrows.appendChild(nextBtn);
    bottom.appendChild(arrows);
    section.appendChild(bottom);

    container.appendChild(section);

    // Scroll logic using CSS transform
    var totalCards = data.variants.length;
    var maxIndex = Math.max(0, totalCards - Math.floor(getVisibleCount()));

    function getCardWidth() {
      var card = track.querySelector(".cf-vp-card");
      if (!card) return 0;
      return card.offsetWidth + 10; // 10 = gap
    }

    function updateTrack() {
      var offset = currentIndex * getCardWidth();
      track.style.transform = "translateX(-" + offset + "px)";
      prevBtn.disabled = currentIndex === 0;
      nextBtn.disabled = currentIndex >= maxIndex;
    }

    nextBtn.addEventListener("click", function () {
      currentIndex = Math.min(currentIndex + getScrollBy(), maxIndex);
      updateTrack();
    });

    prevBtn.addEventListener("click", function () {
      currentIndex = Math.max(currentIndex - getScrollBy(), 0);
      updateTrack();
    });

    // Expose selected variant for cart
    section._getSelectedVPVariantId = function () {
      return selectedVariantId;
    };

    section._clearVPSelection = function () {
      selectedVariantId = null;
      track.querySelectorAll(".cf-vp-card--selected").forEach(function (c) {
        c.classList.remove("cf-vp-card--selected");
      });
    };
  }

  function getVariantPickerItem() {
    var section = document.querySelector("[data-vp-section='true']");
    if (!section || !section._getSelectedVPVariantId) return null;
    var id = section._getSelectedVPVariantId();
    if (!id) return null;
    return { id: parseInt(id), quantity: 1 };
  }

  // ─── 5. COLLECT PROPERTIES (for AJAX injection) ──────────────────────────

  function collectProperties() {
    var props = {};
    var container = document.getElementById("custom-fields-container");
    if (!container) return props;

    var inputs = container.querySelectorAll(
      'input[name^="properties["], select[name^="properties["], textarea[name^="properties["]',
    );
    inputs.forEach(function (input) {
      var match = input.name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      var key = match[1];

      if (input.type === "radio") {
        if (input.checked) {
          props[key] = input.value;
        }
        return;
      }

      var val = (input.value || "").trim();
      if (val) {
        props[key] = val;
      }
    });

    return props;
  }

  /**
   * Inject properties as hidden inputs inside the form.
   * Fallback for when custom-fields-container is outside the cart form.
   */
  function syncPropertiesToForm(form) {
    if (!form) return;
    form.querySelectorAll(".cf-sync-input").forEach(function (el) {
      el.remove();
    });
    var props = collectProperties();
    var keys = Object.keys(props);
    for (var i = 0; i < keys.length; i++) {
      var input = document.createElement("input");
      input.type = "hidden";
      input.name = "properties[" + keys[i] + "]";
      input.value = props[keys[i]];
      input.className = "cf-sync-input";
      form.appendChild(input);
    }
  }

  // ─── 5b. HELPERS: identify main product variants ─────────────────────────

  /** Returns variant IDs of the main product on this page (from pd-data JSON). */
  function getMainProductVariantIds() {
    var pdEl = document.getElementById("pd-data");
    if (!pdEl) return [];
    try {
      var pd = JSON.parse(pdEl.textContent);
      return (pd.variants || []).map(function (v) {
        return parseInt(v.id, 10);
      });
    } catch (e) {
      return [];
    }
  }

  /** Extract first variant ID from a JSON cart/add body string. */
  function extractVariantIdFromBody(body) {
    if (!body || typeof body !== "string") return null;
    try {
      var parsed = JSON.parse(body);
      if (parsed.id) return parseInt(parsed.id, 10) || null;
      if (parsed.items && parsed.items.length > 0 && parsed.items[0].id)
        return parseInt(parsed.items[0].id, 10) || null;
    } catch (e) {}
    return null;
  }

  /**
   * Returns true if the variant belongs to the main product,
   * or if we cannot determine (so we don't skip by mistake).
   */
  function isMainProductVariant(variantId) {
    if (!variantId) return true;
    var ourIds = getMainProductVariantIds();
    if (ourIds.length === 0) return true;
    return ourIds.indexOf(variantId) !== -1;
  }

  // ─── 6. VALIDATION ───────────────────────────────────────────────────────

  function validateFields() {
    let valid = true;
    const missingFields = [];

    // Scope validation to the main product's container only — not recommendation products
    var cfScope = document.getElementById("custom-fields-container");
    if (!cfScope) return true; // no custom fields on this product → pass

    cfScope.querySelectorAll(".cf-error").forEach((e) => e.remove());
    cfScope
      .querySelectorAll(".cf-field-wrapper")
      .forEach((w) => w.classList.remove("cf-field-error"));

    cfScope.querySelectorAll(".cf-field-wrapper").forEach((wrapper) => {
      if (wrapper.style.display === "none") return;
      if (wrapper.classList.contains("cf-combo-hidden")) return;

      // Validate visible required text/select inputs
      const inputs = wrapper.querySelectorAll(
        'input[required]:not([type="radio"]):not([type="hidden"]), select[required]',
      );
      inputs.forEach((input) => {
        if (!input.value || input.value.trim() === "") {
          const label = wrapper.querySelector(".cf-label");
          const fieldName = label
            ? label.textContent.replace(" *", "").trim()
            : "Field";
          missingFields.push(fieldName);

          wrapper.classList.add("cf-field-error");
          const error = document.createElement("div");
          error.className = "cf-error";
          error.textContent = "Please fill in " + fieldName;
          wrapper.appendChild(error);
          valid = false;
        }
      });

      // Validate ALL radio groups — required flag does not matter
      const radios = wrapper.querySelectorAll('input[type="radio"]');
      if (radios.length > 0) {
        const checked = wrapper.querySelector('input[type="radio"]:checked');
        if (!checked) {
          const label = wrapper.querySelector(".cf-label");
          const fieldName = label
            ? label.textContent.replace(" *", "").trim()
            : "Option";
          missingFields.push(fieldName);

          wrapper.classList.add("cf-field-error");
          const error = document.createElement("div");
          error.className = "cf-error";
          error.textContent = "Please select " + fieldName;
          wrapper.appendChild(error);
          valid = false;
        }
      }

      const hiddenColorInput = wrapper.querySelector('input[type="hidden"]');
      if (
        hiddenColorInput &&
        wrapper.querySelector(".cf-swatch-group") &&
        hiddenColorInput.value === ""
      ) {
        const label = wrapper.querySelector(".cf-label");
        const fieldName = label
          ? label.textContent.replace(" *", "").trim()
          : "Color";
        missingFields.push(fieldName);

        wrapper.classList.add("cf-field-error");
        const error = document.createElement("div");
        error.className = "cf-error";
        error.textContent = "Please select " + fieldName;
        wrapper.appendChild(error);
        valid = false;
      }
    });

    if (!valid) {
      showToast("Please fill all required fields before adding to cart.");
    }

    return valid;
  }

  // Clear a wrapper's error UI as soon as the user provides a value.
  function clearWrapperErrorIfFilled(wrapper) {
    if (!wrapper || !wrapper.classList.contains("cf-field-error")) return;

    var hasValue = false;

    var inputs = wrapper.querySelectorAll(
      'input:not([type="radio"]):not([type="hidden"]), select',
    );
    if (inputs.length > 0) {
      var allFilled = true;
      inputs.forEach(function (inp) {
        if (!inp.value || !inp.value.trim()) allFilled = false;
      });
      if (allFilled) hasValue = true;
    }

    var radios = wrapper.querySelectorAll('input[type="radio"]');
    if (radios.length > 0 && wrapper.querySelector('input[type="radio"]:checked')) {
      hasValue = true;
    }

    var hiddenColorInput = wrapper.querySelector('input[type="hidden"]');
    if (
      hiddenColorInput &&
      wrapper.querySelector(".cf-swatch-group") &&
      hiddenColorInput.value !== ""
    ) {
      hasValue = true;
    }

    if (hasValue) {
      wrapper.classList.remove("cf-field-error");
      wrapper.querySelectorAll(".cf-error").forEach(function (e) {
        e.remove();
      });
    }
  }

  // Attach delegated listeners so visible validation errors clear when the user fills a field.
  function attachErrorClearListeners(scope) {
    if (!scope || scope.dataset.cfErrorClearBound === "true") return;
    scope.dataset.cfErrorClearBound = "true";

    var handler = function (e) {
      var wrapper = e.target.closest && e.target.closest(".cf-field-wrapper");
      if (wrapper) clearWrapperErrorIfFilled(wrapper);
    };

    scope.addEventListener("input", handler);
    scope.addEventListener("change", handler);
    scope.addEventListener("click", handler);
  }

  function showToast(message) {
    const existing = document.getElementById("cf-toast");
    if (existing) existing.remove();

    const toast = document.createElement("div");
    toast.id = "cf-toast";
    toast.className = "cf-toast cf-toast--error";
    toast.innerHTML = '<span class="cf-toast-icon">⚠</span> ' + message;
    document.body.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add("cf-toast--visible");
    });

    setTimeout(() => {
      toast.classList.remove("cf-toast--visible");
      setTimeout(() => toast.remove(), 300);
    }, 4000);
  }

  // ─── 7. RECOMMENDATION CARD TRACKING ────────────────────────────────────

  // Recommended product cards hidden after adding to cart — { variantId: liElement }
  var _cfHiddenCards = {};

  // ─── 7b. PACK CARDS (Buy 1 / Buy 2 / Buy 3 — multi-set purchase) ─────
  // Activated when product has "pack-cards" tag. Renders 3 cards at top of
  // custom fields area. Selecting Buy N re-renders the field group N times
  // with unique property names (Set 1 — Name, Set 2 — Name, …).
  // On add-to-cart, one line item per set is sent to /cart/add.js.

  // Always shows 2 decimals so PDP matches Shopify cart exactly
  // (e.g. ₹957.60, ₹399.00) — no rupee-rounding drift.
  function formatPriceINR(cents) {
    var rupees = (cents || 0) / 100;
    return "₹" + rupees.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // Parse admin-entered discount ("30%" or "1099") and compute savings in
  // CENTS (paise). Keeping precision in paise matches Shopify's internal
  // math — earlier version rounded to whole rupees which caused a 0.40
  // mismatch between PDP badge and cart line (₹958 vs ₹957.60).
  function computePackSavingsCents(rawDiscount, basePriceCents, count) {
    if (!rawDiscount) return 0;
    var raw = String(rawDiscount).trim().replace(/^₹\s*/, "");
    if (!raw) return 0;
    if (raw.charAt(raw.length - 1) === "%") {
      var pct = parseFloat(raw.slice(0, -1));
      if (!isFinite(pct) || pct <= 0) return 0;
      return Math.round((basePriceCents * count * pct) / 100);
    }
    var flat = parseFloat(raw);
    if (!isFinite(flat) || flat <= 0) return 0;
    return Math.round(flat * 100);
  }

  function getPackDiscountRaw(count) {
    var container = document.getElementById("custom-fields-container");
    if (!container) return "";
    var key = "pack" + count + "Discount";
    return container.dataset[key] || "";
  }

  function renderPackCards(renderArea) {
    var existing = renderArea.querySelector(".cf-pack-cards");
    if (existing) existing.remove();

    var wrap = document.createElement("div");
    wrap.className = "cf-pack-cards";

    _packConfigs.forEach(function (count) {
      // Use <div> (not <button>) to avoid theme's global button styles
      var card = document.createElement("div");
      card.className =
        "cf-pack-card" + (count === _packCount ? " cf-pack-active" : "");
      card.setAttribute("role", "button");
      card.setAttribute("tabindex", "0");
      card.dataset.pack = count;

      // Save badge (only when discount metafield is set for this pack count)
      var savingsCents = computePackSavingsCents(
        getPackDiscountRaw(count),
        _packBasePriceCents,
        count,
      );
      if (savingsCents > 0) {
        var badge = document.createElement("div");
        badge.className = "cf-pack-badge";
        badge.textContent = "Save " + formatPriceINR(savingsCents);
        card.appendChild(badge);
      }

      var title = document.createElement("div");
      title.className = "cf-pack-title";
      title.textContent = "Buy " + count;
      card.appendChild(title);

      // Show discounted price when there's a saving, else raw base × count
      var totalCents = _packBasePriceCents * count;
      var priceEl = document.createElement("div");
      priceEl.className = "cf-pack-price";
      if (savingsCents > 0) {
        var discountedCents = totalCents - savingsCents;
        if (discountedCents < 0) discountedCents = 0;
        priceEl.textContent = formatPriceINR(discountedCents);
        var strike = document.createElement("span");
        strike.className = "cf-pack-price-strike";
        strike.textContent = " " + formatPriceINR(totalCents);
        priceEl.appendChild(strike);
      } else {
        priceEl.textContent = formatPriceINR(totalCents);
      }
      card.appendChild(priceEl);

      card.addEventListener("click", function () {
        if (count === _packCount) return;
        _packCount = count;
        wrap.querySelectorAll(".cf-pack-card").forEach(function (c) {
          c.classList.toggle(
            "cf-pack-active",
            parseInt(c.dataset.pack, 10) === count,
          );
        });
        renderAllSets(renderArea);
      });

      wrap.appendChild(card);
    });

   // Always insert pack cards as the FIRST child of renderArea so they
    // appear above set groups, addon cards, and variant picker.
    if (renderArea.firstChild) {
      renderArea.insertBefore(wrap, renderArea.firstChild);
    } else {
      renderArea.appendChild(wrap);
    }
  }

  // Render a field with per-set unique property names + element IDs.
  // data-field-id and data-depends-on are LEFT UNCHANGED across sets so that
  // applyAllowedFields (combo-driven show/hide) matches every set. Dependencies
  // between fields are resolved per-set via checkDependencies scope logic.
  function renderFieldForSet(field, setIndex) {
    var wrapper = renderField(field);
    if (!wrapper || setIndex <= 1) return wrapper;

    var suffix = "_set" + setIndex;
    var labelPrefix = "Set " + setIndex + " — ";

    // Rename property inputs so cart properties are unique per set
    wrapper
      .querySelectorAll(
        'input[name^="properties["], select[name^="properties["], textarea[name^="properties["]',
      )
      .forEach(function (inp) {
        inp.name = inp.name.replace(
          /^properties\[(.+)\]$/,
          "properties[" + labelPrefix + "$1]",
        );
      });

    // Suffix HTML element IDs (input id + label htmlFor) so DOM is valid
    wrapper.querySelectorAll("[id]").forEach(function (el) {
      if (el.id && el.id.indexOf("cf_") === 0) {
        el.id = el.id + suffix;
      }
    });
    wrapper.querySelectorAll("label[for]").forEach(function (l) {
      if (l.htmlFor && l.htmlFor.indexOf("cf_") === 0) {
        l.htmlFor = l.htmlFor + suffix;
      }
    });

    return wrapper;
  }

  // Hide any .cf-set-group that has no visible field wrappers so we don't
  // show empty "SET 1 / SET 2" headings on variants without personalization.
  function updateSetVisibility() {
    var groups = document.querySelectorAll(".cf-set-group");
    groups.forEach(function (group) {
      var hasVisibleField = false;
      group.querySelectorAll(".cf-field-wrapper").forEach(function (w) {
        if (w.style.display !== "none") hasVisibleField = true;
      });
      group.style.display = hasVisibleField ? "" : "none";
    });
  }

  function renderAllSets(renderArea) {
    // Remove existing set groups
    renderArea.querySelectorAll(".cf-set-group").forEach(function (g) {
      g.remove();
    });

    // No personalization fields → pack cards run pure for volume discount.
    // Skip set groups so we don't render orphan "Set 1 / Set 2" headings.
    if (!_packFields || _packFields.length === 0) return;

    for (var setIndex = 1; setIndex <= _packCount; setIndex++) {
      var setGroup = document.createElement("div");
      setGroup.className = "cf-set-group";
      setGroup.dataset.setIndex = setIndex;

      if (_packCount > 1) {
        var heading = document.createElement("div");
        heading.className = "cf-set-heading";
        heading.textContent = "Set " + setIndex;
        setGroup.appendChild(heading);
      }

      _packFields.forEach(function (field) {
        var el = renderFieldForSet(field, setIndex);
        if (el) setGroup.appendChild(el);
      });

      renderArea.appendChild(setGroup);
    }

    checkDependencies();

    var container = document.getElementById("custom-fields-container");
    if (container) attachErrorClearListeners(container);

    // Re-apply current variant's allowedFields filter to freshly cloned fields.
    // Triggering the change event on input[name="id"] runs the variant filter
    // logic (setupVariantFieldFilter) which hides fields not allowed by the
    // current combo/variant — e.g. birthstone must stay hidden on "Mini Love".
    var cfForm =
      container && container.closest('form[action*="/cart/add"]');
    var idInput = cfForm ? cfForm.querySelector('input[name="id"]') : null;
    if (idInput) {
      try {
        idInput.dispatchEvent(new Event("change", { bubbles: false }));
      } catch (e) {}
    }
  }

  // Collect only the properties from ONE set group. Strips the "Set N — "
  // prefix so cart line items show clean labels (Name, Message, etc.).
  function collectPropertiesForSet(setIndex) {
    var setGroup = document.querySelector(
      '.cf-set-group[data-set-index="' + setIndex + '"]',
    );
    if (!setGroup) return {};

    var props = {};
    var prefix = setIndex > 1 ? "Set " + setIndex + " — " : "";

    var inputs = setGroup.querySelectorAll(
      'input[name^="properties["], select[name^="properties["], textarea[name^="properties["]',
    );
    inputs.forEach(function (input) {
      var wrapper = input.closest(".cf-field-wrapper");
      if (wrapper && wrapper.style.display === "none") return;

      var m = input.name.match(/^properties\[(.+)\]$/);
      if (!m) return;
      var key = m[1];
      if (prefix && key.indexOf(prefix) === 0) {
        key = key.substring(prefix.length);
      }

      if (input.type === "radio") {
        if (input.checked) props[key] = input.value;
        return;
      }
      var val = (input.value || "").trim();
      if (val) props[key] = val;
    });

    return props;
  }

  // Full reset — bring the form back to its fresh page-load state after a
  // successful add. Pack count → 1, sets rebuilt from scratch, cards
  // repainted with "Buy 1" active. This is what the customer expects: after
  // the items land in the cart, the picker looks like a first-time visit.
  function resetPackFields() {
    var container = document.getElementById("custom-fields-container");
    if (!container) return;
    var renderArea = document.getElementById("cf-render-area");
    if (!renderArea) return;

    // Reset state
    _packCount = 1;

    // Rebuild pack cards (repaints active state on Buy 1) + fresh set groups
    if (_packEnabled) {
      renderPackCards(renderArea);
      renderAllSets(renderArea);
    }

    // Belt-and-braces — clear any lingering error UI outside of sets too
    container.querySelectorAll(".cf-error").forEach(function (e) {
      e.remove();
    });
    container
      .querySelectorAll(".cf-field-wrapper.cf-field-error")
      .forEach(function (w) {
        w.classList.remove("cf-field-error");
      });
  }

  // Manual multi-item add-to-cart used when pack count > 1. Bypasses the
  // regular fetch interceptor via the _cfBypass marker on options.
  function submitPackToCart() {
    var container = document.getElementById("custom-fields-container");
    if (!container) return;
    var mainForm = container.closest('form[action*="/cart/add"]');
    var variantInput = mainForm
      ? mainForm.querySelector('input[name="id"]')
      : null;
    var variantId = variantInput ? parseInt(variantInput.value, 10) : 0;
    if (!variantId) return;

    // Loading state — belt-and-braces approach:
    //   1) Add `.processing` to every cart form (theme's own CSS reveals the
    //      internal <span class="processing"> spinner IF the button has one).
    //   2) Add our own `.cf-processing` class to every ATC button so a CSS
    //      spinner overlay renders even on buttons that lack theme's span
    //      structure. This makes the loader consistent across all products.
    var loadingForms = [];
    document
      .querySelectorAll('form.f8pr, form.f8ps, form[action*="/cart/add"]')
      .forEach(function (f) {
        if (loadingForms.indexOf(f) === -1) loadingForms.push(f);
      });
    var atcBtnSelector =
      '[type="submit"], button[name="add"], .product-form__submit, ' +
      ".overlay-buy_button, .add-to-cart, .sticky-add-to-cart button, " +
      'a[href*="/cart/add"]';
    var loadingBtns = [];
    document.querySelectorAll(atcBtnSelector).forEach(function (b) {
      if (loadingBtns.indexOf(b) === -1) loadingBtns.push(b);
    });
    loadingForms.forEach(function (el) {
      el.classList.add("processing");
    });
    loadingBtns.forEach(function (el) {
      el.classList.add("cf-processing");
    });
    var clearLoading = function () {
      loadingForms.forEach(function (el) {
        el.classList.remove("processing");
      });
      loadingBtns.forEach(function (el) {
        el.classList.remove("cf-processing");
      });
    };

    var items = [];
    for (var i = 1; i <= _packCount; i++) {
      items.push({
        id: variantId,
        quantity: 1,
        properties: collectPropertiesForSet(i),
      });
    }

    var addonItems = getAddonItems();
    var vpItem = getVariantPickerItem();
    if (vpItem) addonItems = addonItems.concat([vpItem]);
    var allItems = items.concat(addonItems);

    // Ask Shopify to include the rendered side-cart section in the response,
    // matching the theme's own bulk-add flow (see custom-assync.js addToCart).
    var opts = {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "xmlhttprequest",
      },
      body: JSON.stringify({ items: allItems, sections: "side-cart" }),
      _cfBypass: true,
    };

    fetch("/cart/add.js", opts)
      .then(function (res) {
        if (!res.ok) throw new Error("cart add failed: " + res.status);
        return res.json();
      })
      .then(function (response) {
        // Clear all set fields so the form is fresh for the next add
        try {
          resetPackFields();
        } catch (e) {
          console.warn("[cf-pack] resetPackFields failed (non-fatal):", e);
        }

        // Refresh side-cart drawer HTML from server so new items are visible,
        // then open drawer. Rover base has no ajaxCart.load(); we swap the
        // <aside id="cart"> element directly and update header count badges.
        var refreshed = false;
        try {
          if (response && response.sections && response.sections["side-cart"]) {
            var parsed = new DOMParser().parseFromString(
              response.sections["side-cart"],
              "text/html",
            );
            var newAside = parsed.querySelector("#cart");
            var oldAside = document.getElementById("cart");
            if (newAside && oldAside) {
              // Preserve outer aside element so ajaxCart's sideCartContainer
              // reference stays valid. Swap only inner HTML.
              oldAside.innerHTML = newAside.innerHTML;
              // Strip .side-cart-initialized so theme's next ajaxCart.load()
              // (triggered by cart panel open) refetches and re-runs
              // handleCartUpdates() to rebind remove/qty listeners.
              oldAside.classList.remove("side-cart-initialized");
              refreshed = true;
            }
            var newQtyEl = parsed.querySelector("[data-totalqty]");
            var newQty = newQtyEl ? newQtyEl.dataset.totalqty || "" : "";
            document
              .querySelectorAll("#cart-count, #cart-count--m")
              .forEach(function (el) {
                el.innerHTML = newQty;
              });
          }
        } catch (e) {
          console.warn("[cf-pack] drawer refresh failed:", e);
        }

        // If drawer refresh worked, open it. Else fall back to /cart page nav.
        if (refreshed) {
          var cartTrigger = document.querySelector('a[data-panel="cart"]');
          if (cartTrigger) {
            cartTrigger.click();
          } else {
            window.location.assign("/cart");
          }
          clearLoading();
        } else {
          window.location.assign("/cart");
        }
      })
      .catch(function (err) {
        console.error("[cf-pack] add-to-cart error:", err);
        showToast("Could not add to cart. Please try again.");
        clearLoading();
      });
  }

  // ─── 8. MAIN INIT ────────────────────────────────────────────────────────

  function init() {
    const container = document.getElementById("custom-fields-container");
    if (!container) return;

    const renderArea = document.getElementById("cf-render-area");
    if (!renderArea || renderArea.dataset.initialized === "true") return;
    renderArea.dataset.initialized = "true";

    const _skeleton = renderArea.querySelector(".cf-skeleton");
    if (_skeleton) _skeleton.remove();

    // Lock width on page load to prevent flex/grid parents from expanding it
    const initialWidth = container.getBoundingClientRect().width;
    container.style.maxWidth = initialWidth + "px";

    const configType = (container.dataset.configType || "").trim().toLowerCase();
    const fields = readFields(container);
    const brands = readBrands(container);
    _brands = brands;
    const rawLabel = container.dataset.brandLabel || "Car";
    _brandLabel =
      rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1).toLowerCase();
    const comboSets = readComboSets(container);
    _comboSets = comboSets;
    const addonCards = readAddonCards(container);

    if (configType === "keychain") {
      renderCarBrands(brands);
    }

    // Check if variants have allowedFields — if yes, skip old combo set system
    var _useVariantFields = false;
    (function () {
      var pdEl = document.getElementById("pd-data");
      if (!pdEl) return;
      try {
        var pd = JSON.parse(pdEl.textContent);
        _useVariantFields = (pd.variants || []).some(function (v) {
          return v.allowedFields && v.allowedFields.trim().length > 0;
        });
      } catch (e) {}
    })();

    if (configType === "hamper" && !_useVariantFields) {
      renderComboSets(comboSets);
    }

    // Pack cards mode (Buy 1 / Buy 2 / Buy 3) — activated by product tag
    _packEnabled = (container.dataset.packCards || "").toLowerCase() === "true";
    _packBasePriceCents =
      parseInt(container.dataset.productBasePrice || "0", 10) || 0;

    if (_packEnabled) {
      _packFields = fields;
      _packCount = 1;
      renderPackCards(renderArea);
      renderAllSets(renderArea);
    } else {
      fields.forEach((field) => {
        const fieldEl = renderField(field);
        renderArea.appendChild(fieldEl);
      });
    }

    // Greeting card addon — renders after all custom fields
    renderAddonCards(addonCards);

    // Variant picker slider
    renderVariantPicker(readVariantPicker());

    container.querySelectorAll(".cf-select").forEach(function (sel) {
      sel.addEventListener("mousedown", function () {
        sel.classList.add("is-open");
      });
      sel.addEventListener("blur", function () {
        sel.classList.remove("is-open");
      });
      sel.addEventListener("change", function () {
        sel.classList.remove("is-open");
      });
    });

    attachErrorClearListeners(container);

    // Intercept all add-to-cart paths: click, submit, fetch, XHR

    // 0a. PACK CARDS take-over — fires before any other handler when active.
    // When Buy N is selected (N>1), we bypass the theme's single-item add and
    // POST N line items ourselves via /cart/add.js.
    if (_packEnabled) {
      document.addEventListener(
        "click",
        function (e) {
          if (_packCount <= 1) return; // Buy 1 → normal flow
          var btn = e.target.closest(
            'form[action="/cart/add"] [type="submit"], ' +
              'form[action="/cart/add"] button[name="add"], ' +
              ".product-form__submit, .overlay-buy_button, " +
              "button[data-add-to-cart], .add-to-cart",
          );
          if (!btn) return;
          var cfC = document.getElementById("custom-fields-container");
          if (!cfC) return;
          var mainForm = cfC.closest('form[action*="/cart/add"]');
          if (mainForm && !mainForm.contains(btn)) return;

          if (!validateFields()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            var firstError = document.querySelector(".cf-error");
            if (firstError) {
              firstError.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
            return;
          }

          e.preventDefault();
          e.stopImmediatePropagation();
          submitPackToCart();
        },
        true,
      );
    }

    // 0. Document-level capture — fires before theme JS (which also uses capture)
    document.addEventListener(
      "click",
      (e) => {
        const btn = e.target.closest(
          'form[action="/cart/add"] [type="submit"], ' +
            'form[action="/cart/add"] button[name="add"], ' +
            ".product-form__submit, .overlay-buy_button, " +
            "button[data-add-to-cart], .add-to-cart",
        );
        if (!btn) return;
        var cfC = document.getElementById("custom-fields-container");
        if (!cfC) return;
        // Find the form that actually contains our custom fields (not the installment form)
        const mainForm = cfC.closest('form[action*="/cart/add"]');
        if (mainForm && !mainForm.contains(btn)) {
          window._cfSkipValidation = true;
          setTimeout(function () {
            window._cfSkipValidation = false;
          }, 3000);
          return;
        }
        if (!validateFields()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          const firstError = document.querySelector(".cf-error");
          if (firstError) {
            firstError.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }
      },
      true,
    );

    // 1. Native form submit
    const cartForm =
      container.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action="/cart/add"]');
    if (cartForm) {
      cartForm.addEventListener(
        "submit",
        (e) => {
          if (!validateFields()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            const firstError = document.querySelector(".cf-error");
            if (firstError) {
              firstError.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
            return;
          }
          syncPropertiesToForm(cartForm);
        },
        true,
      );
    }

    // 2. Add to Cart button click
    const addToCartBtn = cartForm
      ? cartForm.querySelector(
          '[type="submit"], button[name="add"], .product-form__submit',
        )
      : document.querySelector(
          ".product-form__submit, .overlay-buy_button, button[data-add-to-cart], .add-to-cart",
        );
    if (addToCartBtn) {
      addToCartBtn.addEventListener(
        "click",
        (e) => {
          if (!validateFields()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            const firstError = document.querySelector(".cf-error");
            if (firstError) {
              firstError.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }
        },
        true,
      );
    }

    // 3. Buy Now / Dynamic Checkout buttons
    const buyNowBtns = document.querySelectorAll(
      ".shopify-payment-button button, " +
        ".shopify-payment-button__button, " +
        '[data-shopify="payment-button"] button, ' +
        ".dynamic-checkout__button",
    );
    buyNowBtns.forEach((btn) => {
      btn.addEventListener(
        "click",
        (e) => {
          if (!validateFields()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            const firstError = document.querySelector(".cf-error");
            if (firstError) {
              firstError.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }
        },
        true,
      );
    });

    // 4. Fetch API intercept — block AJAX cart/add requests + inject properties
    //
    // Handles three body formats:
    //   A) fetch(stringUrl, { body: jsonString })  — most common
    //   B) fetch(stringUrl, { body: formData })    — some themes
    //   C) fetch(RequestObject)                    — Horizon / modern themes
    //
    const originalFetch = window.fetch;
    window.fetch = function (url, options) {
      var urlStr = "";
      var isRequestObject = false;
      if (typeof url === "string") {
        urlStr = url;
      } else if (url && typeof url === "object" && url.url) {
        urlStr = url.url;
        isRequestObject = true;
      }

      if (!urlStr.includes("/cart/add")) {
        return originalFetch.apply(this, arguments);
      }

      // Pack cards multi-item submit — bypass all our injection logic
      if (options && options._cfBypass) {
        return originalFetch.apply(this, arguments);
      }

      var pendingProps = window._cfPendingProps || null;
      if (pendingProps) {
        window._cfPendingProps = null;
        window._cfSkipValidation = false;
      } else if (
        window._cfSkipValidation === true ||
        !document.getElementById("custom-fields-container")
      ) {
        return originalFetch.apply(this, arguments);
      } else {
        // If this cart/add is for a different product (not the main personalized product),
        // pass through without validating or injecting main product properties.
        var reqBody =
          options && typeof options.body === "string" ? options.body : null;
        if (
          reqBody &&
          !isMainProductVariant(extractVariantIdFromBody(reqBody))
        ) {
          return originalFetch.apply(this, arguments);
        }

        if (!validateFields()) {
          setTimeout(function () {
            var firstError = document.querySelector(".cf-error");
            if (firstError) {
              firstError.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 50);
          return Promise.reject(
            new Error("Validation failed: Please fill all required fields."),
          );
        }
      }

      var properties = pendingProps || collectProperties();
      var propKeys = Object.keys(properties);
      var addonItems = getAddonItems();
      var vpItem = getVariantPickerItem();
      if (vpItem) addonItems = addonItems.concat([vpItem]);

      function injectIntoJson(jsonStr) {
        try {
          var parsed = JSON.parse(jsonStr);
          var injected = false;

          // Single item format → convert to items array so we can append addons
          if ((parsed.id || parsed.quantity) && !parsed.items) {
            parsed = {
              items: [
                {
                  id: parsed.id,
                  quantity: parsed.quantity || 1,
                  properties: Object.assign(
                    {},
                    parsed.properties || {},
                    properties,
                  ),
                  selling_plan: parsed.selling_plan,
                },
              ],
            };
            injected = true;
          } else if (parsed.items && Array.isArray(parsed.items)) {
            parsed.items.forEach(function (item) {
              item.properties = Object.assign(
                {},
                item.properties || {},
                properties,
              );
            });
            injected = true;
          }

          // Append selected addon cards to items array
          if (addonItems.length > 0) {
            parsed.items = (parsed.items || []).concat(addonItems);
            injected = true;
          }

          if (injected) {
            console.log(
              "[CF] ✅ Injected properties + addon items:",
              properties,
              addonItems,
            );
            return JSON.stringify(parsed);
          }
        } catch (e) {}
        return null;
      }

      function injectIntoFormData(fd) {
        for (var i = 0; i < propKeys.length; i++) {
          fd.set("properties[" + propKeys[i] + "]", properties[propKeys[i]]);
        }
        console.log("[CF] ✅ Properties INJECTED into FormData:", properties);
      }

      // If nothing to inject, pass through
      if (propKeys.length === 0 && addonItems.length === 0) {
        console.log("[CF] No properties or addon items — skipping");
        return originalFetch.apply(this, arguments);
      }

      // Helper: add addon items via separate fetch call
      function sendAddonItemsSeparately() {
        if (addonItems.length === 0) return Promise.resolve();
        console.log("[CF] Sending addon items separately:", addonItems);
        return originalFetch(window.location.origin + "/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: addonItems }),
        }).then(function () {
          var _s = document.querySelector("[data-addon-section='true']");
          if (_s && _s._clearSelections) _s._clearSelections();
          var _v = document.querySelector("[data-vp-section='true']");
          if (_v && _v._clearVPSelection) _v._clearVPSelection();
        });
      }

      // Case C: Request object
      if (isRequestObject) {
        console.log("[CF] Detected Request object — cloning and injecting...");
        var req = url;
        return req
          .clone()
          .text()
          .then(
            function (bodyText) {
              var newBody = injectIntoJson(bodyText);
              if (newBody) {
                var newReq = new Request(req.url, {
                  method: req.method,
                  headers: req.headers,
                  body: newBody,
                  mode: req.mode,
                  credentials: req.credentials,
                  cache: req.cache,
                  redirect: req.redirect,
                  referrer: req.referrer,
                  integrity: req.integrity,
                });
                return originalFetch.call(this, newReq);
              }
              // Body was not JSON (e.g. FormData) — add addon items separately
              console.warn(
                "[CF] ⚠ Could not inject into Request body — sending addons separately",
              );
              return sendAddonItemsSeparately().then(
                function () {
                  return originalFetch.call(this, req);
                }.bind(this),
              );
            }.bind(this),
          );
      }

      // Case A & B: String URL + options
      var fetchOptions = options ? Object.assign({}, options) : {};
      var body = fetchOptions.body;

      if (body && typeof body === "string") {
        var newBody = injectIntoJson(body);
        if (newBody) {
          fetchOptions.body = newBody;
        } else {
          console.warn("[CF] ⚠ JSON body found but injection failed");
        }
      } else if (body instanceof FormData) {
        injectIntoFormData(body);
        fetchOptions.body = body;
        // Add addon items FIRST so they are already in cart when drawer opens
        if (addonItems.length > 0) {
          var self = this;
          return originalFetch(window.location.origin + "/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: addonItems }),
          }).then(function () {
            // Clear selections so next Add to Cart starts fresh
            var addonSection = document.querySelector(
              "[data-addon-section='true']",
            );
            if (addonSection && addonSection._clearSelections)
              addonSection._clearSelections();
            var vpSection = document.querySelector("[data-vp-section='true']");
            if (vpSection && vpSection._clearVPSelection)
              vpSection._clearVPSelection();
            console.log("[CF] Addon items added — now adding main product");
            return originalFetch.call(self, urlStr, fetchOptions);
          });
        }
      } else {
        console.warn(
          "[CF] ⚠ Unknown body type:",
          body ? typeof body : "no body",
          "— properties NOT injected",
        );
      }

      return originalFetch.call(this, urlStr, fetchOptions);
    };

    // 5. XMLHttpRequest intercept — for older themes
    const originalXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (_method, url) {
      this._cfUrl = url;
      return originalXHROpen.apply(this, arguments);
    };
    const originalXHRSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (body) {
      if (this._cfUrl && this._cfUrl.includes("/cart/add")) {
        // Skip entirely for non-main products (different variant ID in body)
        if (window._cfSkipValidation === true) {
          return originalXHRSend.call(this, body);
        }
        var xhrBodyStr = typeof body === "string" ? body : null;
        if (
          xhrBodyStr &&
          !isMainProductVariant(extractVariantIdFromBody(xhrBodyStr))
        ) {
          return originalXHRSend.call(this, body);
        }
        if (
          document.getElementById("custom-fields-container") &&
          !validateFields()
        ) {
          setTimeout(function () {
            var firstError = document.querySelector(".cf-error");
            if (firstError) {
              firstError.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 50);
          this.abort();
          return;
        }
        try {
          var properties = collectProperties();
          var xhrAddonItems = getAddonItems();
          var xhrVpItem = getVariantPickerItem();
          if (xhrVpItem) xhrAddonItems = xhrAddonItems.concat([xhrVpItem]);

          if (typeof body === "string") {
            var parsed = JSON.parse(body);
            // Inject properties
            if (Object.keys(properties).length > 0) {
              if (parsed.id && !parsed.items) {
                parsed.properties = Object.assign(
                  {},
                  parsed.properties || {},
                  properties,
                );
              }
              if (parsed.items && Array.isArray(parsed.items)) {
                parsed.items.forEach(function (item) {
                  item.properties = Object.assign(
                    {},
                    item.properties || {},
                    properties,
                  );
                });
              }
            }
            // Append addon + variant picker items
            if (xhrAddonItems.length > 0) {
              if (parsed.id && !parsed.items) {
                // Convert single-item format to items array
                parsed = {
                  items: [
                    {
                      id: parsed.id,
                      quantity: parsed.quantity || 1,
                      properties: Object.assign(
                        {},
                        parsed.properties || {},
                        properties,
                      ),
                      selling_plan: parsed.selling_plan,
                    },
                  ],
                };
              }
              parsed.items = (parsed.items || []).concat(xhrAddonItems);
              var _xhrAddonSec = document.querySelector(
                "[data-addon-section='true']",
              );
              if (_xhrAddonSec && _xhrAddonSec._clearSelections)
                _xhrAddonSec._clearSelections();
              var _xhrVpSec = document.querySelector(
                "[data-vp-section='true']",
              );
              if (_xhrVpSec && _xhrVpSec._clearVPSelection)
                _xhrVpSec._clearVPSelection();
            }
            body = JSON.stringify(parsed);
          } else if (xhrAddonItems.length > 0) {
            // FormData body — send addon items via separate fetch (background)
            var _xhrOrigFetch = window._cfOriginalFetch || originalFetch;
            _xhrOrigFetch(window.location.origin + "/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ items: xhrAddonItems }),
            }).then(function () {
              var _s = document.querySelector("[data-addon-section='true']");
              if (_s && _s._clearSelections) _s._clearSelections();
              var _v = document.querySelector("[data-vp-section='true']");
              if (_v && _v._clearVPSelection) _v._clearVPSelection();
            });
          }
        } catch (e) {}
      }
      return originalXHRSend.call(this, body);
    };

    // Hide Buy Now button on products with custom fields
    var paymentButtons = document.querySelector(".shopify-payment-button");
    if (paymentButtons) {
      paymentButtons.style.display = "none";
    }

    checkDependencies();

    // Hamper with multiple combos: auto-select first combo on load (only old flow)
    if (configType === "hamper" && comboSets.length > 1 && !_useVariantFields) {
      applyAllowedFields(comboSets[0].allowedIds);
    }

    // ── VARIANT-BASED FIELD FILTERING ──
    // When variant changes, filter personalized fields + show included items
    // based on variant metafields (allowedFields, includedItems) from pd-data.
    (function setupVariantFieldFilter() {
      var pdEl = document.getElementById("pd-data");
      if (!pdEl) return;
      var pd;
      try { pd = JSON.parse(pdEl.textContent); } catch (e) { return; }
      if (!pd.variants || pd.variants.length < 2) return;

      // Check if any variant has allowedFields or includedItems — if neither, skip entirely
      var hasAllowedFields = pd.variants.some(function (v) {
        return v.allowedFields && v.allowedFields.trim().length > 0;
      });
      var hasIncludedItems = pd.variants.some(function (v) {
        return v.includedItems && v.includedItems.length > 0;
      });
      if (!hasAllowedFields && !hasIncludedItems) return;

      function parseAllowedIds(rawStr) {
        // Blank metafield → return a non-matching sentinel so applyAllowedFields
        // hides every custom field instead of falling through to "show all".
        if (!rawStr || !rawStr.trim()) return ["__cf_no_match__"];
        var ids = rawStr
          .replace(/<br\s*\/?>/gi, "\n")
          .split("\n")
          .map(function (l) { return l.trim(); })
          .filter(function (l) { return l.length > 0; })
          .map(function (label) {
            return label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
          });
        return ids.length > 0 ? ids : ["__cf_no_match__"];
      }

      function applyVariantData(variantId) {
        var variant = pd.variants.find(function (v) { return v.id === variantId; });
        if (!variant) return;

        // 1. Filter fields — always apply so blank/non-matching variants
        // correctly hide all custom fields and don't leave stale state.
        var ids = parseAllowedIds(variant.allowedFields);
        applyAllowedFields(ids);

        // 2. Show included items preview
        if (variant.includedItems && variant.includedItems.length > 0) {
          showVariantIncludedItems(variant.includedItems);
        } else {
          hideVariantIncludedItems();
        }

        checkDependencies();
      }

      // Included items preview (reuses combo preview styling)
      function showVariantIncludedItems(items) {
        var existing = document.getElementById("cf-variant-items-preview");
        if (existing) existing.remove();

        var previewArea = document.createElement("div");
        previewArea.id = "cf-variant-items-preview";
        previewArea.className = "cf-combo-preview";

        var offerEl = document.getElementById("cf-offer-text");
        if (offerEl && offerEl.innerHTML.trim()) {
          var complimentary = document.createElement("div");
          complimentary.className = "cf-combo-complimentary";
          complimentary.innerHTML = offerEl.innerHTML;
          previewArea.appendChild(complimentary);
        }

        var heading = document.createElement("div");
        heading.className = "cf-combo-preview-heading";
        heading.textContent = "Selected Items";
        previewArea.appendChild(heading);

        var row = document.createElement("div");
        row.className = "cf-combo-horizontal";

        items.forEach(function (item) {
          var card = document.createElement("div");
          card.className = "cf-combo-hcard";
          if (item.image) {
            var imgWrap = document.createElement("div");
            imgWrap.className = "cf-combo-hcard-img-wrap";
            var img = document.createElement("img");
            img.src = item.image;
            img.alt = item.name;
            img.className = "cf-combo-hcard-img";
            imgWrap.appendChild(img);
            card.appendChild(imgWrap);
          }
          var textWrap = document.createElement("div");
          textWrap.className = "cf-combo-hcard-text";
          var name = document.createElement("div");
          name.className = "cf-combo-hcard-name";
          name.textContent = item.name;
          textWrap.appendChild(name);
          if (item.note) {
            var note = document.createElement("div");
            note.textContent = item.note;
            textWrap.appendChild(note);
          }
          var checkBadge = document.createElement("div");
          checkBadge.className = "cf-combo-hcard-check";
          checkBadge.innerHTML = "\u2714";
          card.appendChild(textWrap);
          card.appendChild(checkBadge);
          row.appendChild(card);
        });

        previewArea.appendChild(row);
        previewArea.style.display = "block";

        // Insert BEFORE pack cards (if any), else before the first field wrapper.
        // When fields are nested inside a .cf-set-group, walk up to the
        // top-level ancestor so insertBefore doesn't throw NotFoundError.
        var packCards = renderArea.querySelector(".cf-pack-cards");
        if (packCards) {
          renderArea.insertBefore(previewArea, packCards);
          return;
        }
        var firstField = renderArea.querySelector(".cf-field-wrapper");
        if (firstField) {
          var ref = firstField;
          while (ref && ref.parentNode !== renderArea) {
            ref = ref.parentNode;
          }
          if (ref && ref.parentNode === renderArea) {
            renderArea.insertBefore(previewArea, ref);
          } else {
            renderArea.appendChild(previewArea);
          }
        } else {
          renderArea.appendChild(previewArea);
        }
      }

      function hideVariantIncludedItems() {
        var existing = document.getElementById("cf-variant-items-preview");
        if (existing) existing.remove();
      }

      // Listen for variant changes — theme updates input[name="id"]
      var cartForm = container.closest('form[action*="/cart/add"]') ||
                     document.querySelector('form[action="/cart/add"]');
      if (cartForm) {
        var idInput = cartForm.querySelector('input[name="id"]');
        if (idInput) {
          // MutationObserver to catch theme JS updating the value
          new MutationObserver(function () {
            var vid = parseInt(idInput.value, 10);
            if (vid) applyVariantData(vid);
          }).observe(idInput, { attributes: true, attributeFilter: ["value"] });

          // Also listen for change event (some themes dispatch it)
          idInput.addEventListener("change", function () {
            var vid = parseInt(idInput.value, 10);
            if (vid) applyVariantData(vid);
          });

          // Apply for the initially selected variant
          var initialVid = parseInt(idInput.value, 10);
          if (initialVid) applyVariantData(initialVid);
        }
      }
    })();

    // Strip theme select plugin wrappers to keep all cf-select elements native
    (function () {
      var _cfStrippingAll = false;
      function stripAllCfSelects() {
        if (_cfStrippingAll) return;
        _cfStrippingAll = true;
        renderArea.querySelectorAll(".select-wrapper").forEach(function (wrap) {
          var sel = wrap.querySelector(".cf-select");
          if (!sel) return;
          sel.classList.remove(
            "hidden",
            "semantic-select-initialized",
            "select-init",
            "changed",
          );
          wrap.parentNode.insertBefore(sel, wrap);
          wrap.remove();
        });
        setTimeout(function () {
          _cfStrippingAll = false;
        }, 100);
      }
      stripAllCfSelects();
      var obs = new MutationObserver(stripAllCfSelects);
      obs.observe(renderArea, { childList: true, subtree: true });
    })();

    // Restore recommendation cards when their product is removed from cart
    document.addEventListener("cart:update", function () {
      if (Object.keys(_cfHiddenCards).length === 0) return;
      fetch("/cart.js")
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var inCart = (cart.items || []).map(function (item) {
            return String(item.variant_id);
          });
          Object.keys(_cfHiddenCards).forEach(function (varId) {
            if (inCart.indexOf(varId) === -1) {
              var card = _cfHiddenCards[varId];
              if (card) {
                card.style.display = "";
                card.style.opacity = "1";
              }
              delete _cfHiddenCards[varId];
            }
          });
        });
    });

    // Pack cards re-render guard — theme's variant AJAX can wipe #cf-render-area.
    // Observe container and re-inject pack cards + sets if they disappear.
    if (_packEnabled) {
      var _packRenderTimer = null;
      var packObserver = new MutationObserver(function () {
        var currentArea = document.getElementById("cf-render-area");
        if (!currentArea) return;
        if (currentArea.querySelector(".cf-pack-cards")) return;
        // Debounce to avoid re-render loops during theme AJAX transitions
        if (_packRenderTimer) clearTimeout(_packRenderTimer);
        _packRenderTimer = setTimeout(function () {
          var area = document.getElementById("cf-render-area");
          if (!area || area.querySelector(".cf-pack-cards")) return;
          renderPackCards(area);
          renderAllSets(area);
        }, 80);
      });
      packObserver.observe(container, {
        childList: true,
        subtree: true,
      });
    }

    watchQuickBuyDrawer();
  }

  // ─── 9. QUICK-BUY DRAWER FIELD INJECTION ────────────────────────────────

  /** Collect properties from a specific element (same as collectProperties but scoped). */
  function collectFromElement(el) {
    var props = {};
    el.querySelectorAll(
      'input[name^="properties["], select[name^="properties["]',
    ).forEach(function (inp) {
      var m = inp.name.match(/^properties\[(.+)\]$/);
      if (!m) return;
      if (inp.type === "radio") {
        if (inp.checked) props[m[1]] = inp.value;
      } else {
        var v = (inp.value || "").trim();
        if (v) props[m[1]] = v;
      }
    });
    return props;
  }

  /**
   * Watch for new form[action*="/cart/add"] elements added to the DOM
   * (quick-buy drawers). Inject custom fields and intercept the Add to Cart button.
   */
  function watchQuickBuyDrawer() {
    // Collect ALL initial forms on the main product page (installment + cart)
    var mainForms = [];
    document
      .querySelectorAll('form[action*="/cart/add"]')
      .forEach(function (f) {
        mainForms.push(f);
      });

    // PERF: Only inspect mutations.addedNodes instead of full document scan
    // on every DOM change. Body+subtree observer still fires on every change,
    // but callback now does targeted work — collects only forms added in this
    // batch (plus content added inside already-bound forms for race condition).
    var observer = new MutationObserver(function (mutations) {
      // Collect unique forms to process this batch
      var formsToProcess = [];
      function addFormOnce(f) {
        if (formsToProcess.indexOf(f) === -1) formsToProcess.push(f);
      }

      for (var mi = 0; mi < mutations.length; mi++) {
        var addedNodes = mutations[mi].addedNodes;
        for (var ni = 0; ni < addedNodes.length; ni++) {
          var node = addedNodes[ni];
          if (node.nodeType !== 1) continue; // skip text/comment nodes

          // Form added directly
          if (node.matches && node.matches('form[action*="/cart/add"]')) {
            addFormOnce(node);
          }
          // Form inside the added subtree
          if (node.querySelectorAll) {
            node.querySelectorAll('form[action*="/cart/add"]').forEach(addFormOnce);
          }
          // Content added INSIDE an already-bound form (race condition: render
          // area populates after form was bound — original code caught this
          // via repeated full scans; we catch it via parent lookup).
          if (node.closest) {
            var parentForm = node.closest('form[action*="/cart/add"]');
            if (parentForm && parentForm.dataset.cfDrawerBound) {
              addFormOnce(parentForm);
            }
          }
        }
      }

      if (formsToProcess.length === 0) return;

      formsToProcess.forEach(function (form) {
        if (mainForms.indexOf(form) !== -1) return;

        // Re-check already-bound forms whose render area is still empty.
        // This handles race conditions where the form was processed before
        // the theme finished loading its content (e.g., quickshop AJAX).
        if (form.dataset.cfDrawerBound) {
          var cf = form.querySelector("#custom-fields-container");
          if (cf) {
            var ra = cf.querySelector("#cf-render-area");
            if (ra && ra.dataset.initialized !== "true" && ra.children.length === 0) {
              _initDrawerOwnContainer(form, cf, ra);
            }
          }
          return;
        }

        // If this form is inside the main product section AND one of
        // the original mainForms is no longer in the DOM, it's a
        // section re-render (variant change) — not a drawer.
        var isMainProductSection = form.closest(
          "#main-product:not([class*='quickshop']), .m6pr:not([class*='quickshop'])",
        );
        if (isMainProductSection) {
          var anyMainFormGone = mainForms.some(function (f) {
            return !document.body.contains(f);
          });
          if (anyMainFormGone) {
            // Replace stale refs with new form
            mainForms = [];
            document
              .querySelectorAll('form[action*="/cart/add"]')
              .forEach(function (f) {
                var inMain = f.closest(
                  "#main-product:not([class*='quickshop']), .m6pr:not([class*='quickshop'])",
                );
                if (inMain) mainForms.push(f);
              });
            return;
          }
          // Form is inside the main product section and all mainForms are
          // still in DOM — this is a SECOND form on the same page (e.g.
          // installment form). Track it but don't inject fields.
          mainForms.push(form);
          return;
        }

        // Process the form — but only mark as bound if content is ready.
        // If the form is in the quickshop and #custom-fields-container hasn't
        // been populated yet, defer so the next mutation can retry.
        var result = injectFieldsIntoDrawerForm(form);
        if (result !== false) {
          form.dataset.cfDrawerBound = "true";
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * If the drawer has its own #custom-fields-container (different product AJAX-loaded),
   * render fields directly from that container's data instead of cloning.
   */
  function _initDrawerOwnContainer(form, cfContainer, renderArea) {
    if (renderArea.dataset.initialized === "true") return;
    renderArea.dataset.initialized = "true";

    // Remove the keychain skeleton placeholder (server-rendered in liquid)
    // before populating real fields. Mirrors the main page init().
    var _skel = renderArea.querySelector(".cf-skeleton");
    if (_skel) _skel.remove();

    var configType = (cfContainer.dataset.configType || "").trim().toLowerCase();
    var fields = readFields(cfContainer);
    var brands = readBrands(cfContainer);
    var comboSets = readComboSets(cfContainer);

    if (configType === "keychain") {
      renderCarBrands(brands, renderArea, cfContainer);
    }

    // Check if variants have allowedFields — skip old combo set if yes.
    // Also check includedItems — products may have items to show per variant
    // even when no field filtering is needed (e.g. simple "gift options"
    // products like Couple Scent Candle Set).
    var _drawerUseVariantFields = false;
    var _drawerHasVariantItems = false;
    var _drawerPdData = null;
    (function () {
      // Scope pd-data lookup to the form first — quickshop has its own
      // form+pd-data, and document.getElementById would return the main
      // page's pd-data (wrong product's variants).
      var pdEl = form.querySelector("#pd-data") ||
                 cfContainer.querySelector("#pd-data") ||
                 document.getElementById("pd-data");
      if (!pdEl) return;
      try {
        _drawerPdData = JSON.parse(pdEl.textContent);
        _drawerUseVariantFields = (_drawerPdData.variants || []).some(function (v) {
          return v.allowedFields && v.allowedFields.trim().length > 0;
        });
        _drawerHasVariantItems = (_drawerPdData.variants || []).some(function (v) {
          return v.includedItems && v.includedItems.length > 0;
        });
      } catch (e) {}
    })();

    if (configType === "hamper" && !_drawerUseVariantFields) {
      renderComboSets(comboSets, renderArea);
    }

    fields.forEach(function (field) {
      renderArea.appendChild(renderField(field));
    });

    renderArea.querySelectorAll(".cf-select").forEach(function (sel) {
      sel.addEventListener("mousedown", function () {
        sel.classList.add("is-open");
      });
      sel.addEventListener("blur", function () {
        sel.classList.remove("is-open");
      });
      sel.addEventListener("change", function () {
        sel.classList.remove("is-open");
      });
    });

    stripPluginWrappers(cfContainer);
    initDrawerDependencies(renderArea);
    attachErrorClearListeners(renderArea);

    // Variant-based field filtering / included items preview for drawer.
    // Fire if EITHER allowedFields (field filtering) OR includedItems
    // (items preview) exist — matching the main page's setupVariantFieldFilter.
    if ((_drawerUseVariantFields || _drawerHasVariantItems) && _drawerPdData) {
      _setupDrawerVariantFilter(form, renderArea, _drawerPdData);
    }

    var addBtn = form.querySelector(
      '[type="submit"], button[name="add"], .product-form__submit',
    );
    if (!addBtn) return;

    addBtn.addEventListener(
      "click",
      function (e) {
        if (!validateDrawerFields(renderArea)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        window._cfPendingProps = collectFromElement(renderArea);
      },
      true,
    );
  }

  /**
   * Variant-based field filtering for drawer forms.
   * Listens for variant change and applies allowedFields + includedItems.
   */
  function _setupDrawerVariantFilter(form, renderArea, pd) {
    if (!pd || !pd.variants) return;

    function parseAllowedIds(rawStr) {
      // Blank metafield → return a non-matching sentinel so applyAllowedFields
      // hides every custom field instead of falling through to "show all".
      if (!rawStr || !rawStr.trim()) return ["__cf_no_match__"];
      var ids = rawStr
        .replace(/<br\s*\/?>/gi, "\n")
        .split("\n")
        .map(function (l) { return l.trim(); })
        .filter(function (l) { return l.length > 0; })
        .map(function (label) {
          return label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
        });
      return ids.length > 0 ? ids : ["__cf_no_match__"];
    }

    function applyVariantToDrawer(variantId) {
      var variant = pd.variants.find(function (v) { return v.id === variantId; });
      if (!variant) return;

      // Filter fields — always apply so blank/non-matching variants correctly
      // hide all custom fields and don't leave stale state from a prior variant.
      var ids = parseAllowedIds(variant.allowedFields);
      applyAllowedFields(ids, renderArea);

      // Included items preview
      var existing = renderArea.querySelector("#cf-drawer-items-preview");
      if (existing) existing.remove();

      if (variant.includedItems && variant.includedItems.length > 0) {
        var previewArea = document.createElement("div");
        previewArea.id = "cf-drawer-items-preview";
        previewArea.className = "cf-combo-preview";

        var offerEl = document.getElementById("cf-offer-text");
        if (offerEl && offerEl.innerHTML.trim()) {
          var complimentary = document.createElement("div");
          complimentary.className = "cf-combo-complimentary";
          complimentary.innerHTML = offerEl.innerHTML;
          previewArea.appendChild(complimentary);
        }

        var heading = document.createElement("div");
        heading.className = "cf-combo-preview-heading";
        heading.textContent = "Selected Items";
        previewArea.appendChild(heading);

        var row = document.createElement("div");
        row.className = "cf-combo-horizontal";

        variant.includedItems.forEach(function (item) {
          var card = document.createElement("div");
          card.className = "cf-combo-hcard";
          if (item.image) {
            var imgWrap = document.createElement("div");
            imgWrap.className = "cf-combo-hcard-img-wrap";
            var img = document.createElement("img");
            img.src = item.image;
            img.alt = item.name;
            img.className = "cf-combo-hcard-img";
            imgWrap.appendChild(img);
            card.appendChild(imgWrap);
          }
          var textWrap = document.createElement("div");
          textWrap.className = "cf-combo-hcard-text";
          var name = document.createElement("div");
          name.className = "cf-combo-hcard-name";
          name.textContent = item.name;
          textWrap.appendChild(name);
          if (item.note) {
            var note = document.createElement("div");
            note.textContent = item.note;
            textWrap.appendChild(note);
          }
          var checkBadge = document.createElement("div");
          checkBadge.className = "cf-combo-hcard-check";
          checkBadge.innerHTML = "\u2714";
          card.appendChild(textWrap);
          card.appendChild(checkBadge);
          row.appendChild(card);
        });

        previewArea.appendChild(row);
        previewArea.style.display = "block";

        var firstField = renderArea.querySelector(".cf-field-wrapper");
        if (firstField) {
          renderArea.insertBefore(previewArea, firstField);
        } else {
          renderArea.appendChild(previewArea);
        }
      }

      checkDrawerDependencies(renderArea);
    }

    var idInput = form.querySelector('input[name="id"]');
    if (idInput) {
      new MutationObserver(function () {
        var vid = parseInt(idInput.value, 10);
        if (vid) applyVariantToDrawer(vid);
      }).observe(idInput, { attributes: true, attributeFilter: ["value"] });

      idInput.addEventListener("change", function () {
        var vid = parseInt(idInput.value, 10);
        if (vid) applyVariantToDrawer(vid);
      });

      var initialVid = parseInt(idInput.value, 10);
      if (initialVid) applyVariantToDrawer(initialVid);
    }
  }

  /**
   * Fetch a different product's page, extract its #custom-fields-container,
   * and inject + initialize it inside the drawer form.
   */
  function _fetchAndInjectDrawerFields(form) {
    // Walk up from the form to find a product link in the same container
    var productUrl = null;
    var parent = form.parentElement;
    while (parent && parent !== document.body) {
      var link = parent.querySelector('a[href*="/products/"]');
      if (link) {
        // Extract clean pathname (strip query params)
        try { productUrl = new URL(link.href, window.location.origin).pathname; } catch(e) {}
        break;
      }
      parent = parent.parentElement;
    }

    if (!productUrl) return;

    fetch(productUrl)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, "text/html");
        var remoteCf = doc.querySelector("#custom-fields-container");
        if (!remoteCf) return;

        // Inject the container into the form
        var submitBtn = form.querySelector(
          '[type="submit"], button[name="add"], .product-form__submit',
        );
        if (submitBtn) {
          form.insertBefore(remoteCf, submitBtn);
        } else {
          form.appendChild(remoteCf);
        }

        var renderArea = remoteCf.querySelector("#cf-render-area");
        if (renderArea) {
          _initDrawerOwnContainer(form, remoteCf, renderArea);
        }
      })
      .catch(function (e) {
        console.warn("[CF] Failed to fetch drawer product fields:", e);
      });
  }

  function injectFieldsIntoDrawerForm(form) {

    // Skip cart drawer upsell ("You may also like") recommendations.
    // These should behave as plain product cards — the user navigates to the
    // product page (or quickshop) to fill custom fields. Injecting fields
    // inline here was leaking the quickshop fix into the cart drawer.
    if (form.closest(".cart-upsell")) {
      return;
    }
    
    // Skip installment forms — they don't need custom fields, and processing
    // them causes double fields in the quickshop drawer.
    if (
      form.classList.contains("f8pr-product-form-installment") ||
      (form.getAttribute("id") && form.getAttribute("id").indexOf("installment") !== -1)
    ) {
      var cfInInstallment = form.querySelector("#custom-fields-container");
      if (cfInInstallment) cfInInstallment.style.display = "none";
      return;
    }

    // Case 1: Drawer has its own custom-fields data (different AJAX-loaded product)
    var ownContainer = form.querySelector("#custom-fields-container");
    if (ownContainer) {
      var ownRenderArea = ownContainer.querySelector("#cf-render-area");
      if (ownRenderArea) {
        _initDrawerOwnContainer(form, ownContainer, ownRenderArea);
      }
      return; // bound
    }

    // If form is inside #quickshop but has no container yet, the theme may
    // still be loading content via AJAX.  Return false so the observer
    // does NOT set data-cf-drawer-bound — it will retry on the next mutation.
    if (form.closest("#quickshop")) {
      return false; // defer — content not ready
    }

    // Case 2: Same product — clone the main page render area
    var renderArea = document.getElementById("cf-render-area");
    if (!renderArea || !renderArea.children.length) return;

    // Skip if the drawer is loading a different product
    var varInput = form.querySelector('input[name="id"], select[name="id"]');
    var drawerId = varInput ? parseInt(varInput.value) : null;
    if (drawerId && drawerId > 0) {
      var pdEl = document.getElementById("pd-data");
      if (pdEl) {
        try {
          var pd = JSON.parse(pdEl.textContent);
          var ourIds = (pd.variants || []).map(function (v) {
            return v.id;
          });
          if (ourIds.length > 0 && ourIds.indexOf(drawerId) === -1) {
            // Different product — fetch its page and inject custom fields
            var skipBtn = form.querySelector(
              '[type="submit"], button[name="add"], .product-form__submit',
            );
            if (skipBtn && !skipBtn.dataset.cfSkipBound) {
              skipBtn.dataset.cfSkipBound = "true";
              skipBtn.addEventListener(
                "click",
                function () {
                  window._cfSkipValidation = true;
                  setTimeout(function () {
                    window._cfSkipValidation = false;
                  }, 5000);
                  // Hide the recommendation card after adding to cart
                  var card = skipBtn.closest("li");
                  if (card) {
                    _cfHiddenCards[String(drawerId)] = card;
                    card.style.transition = "opacity 0.3s ease";
                    card.style.opacity = "0";
                    setTimeout(function () {
                      card.style.display = "none";
                    }, 300);
                  }
                },
                true,
              );
            }

            // Fetch the different product's page to get its custom fields
            _fetchAndInjectDrawerFields(form);
            return;
          }
        } catch (e) {}
      }
    }

    var clone = document.createElement("div");
    clone.id = "cf-render-area-drawer";
    clone.className = "cf-render-area-clone";
    clone.style.cssText =
      "margin-top:12px;width:100%;max-width:100%;box-sizing:border-box;overflow:hidden;";
    clone.innerHTML = renderArea.innerHTML;

    var submitBtn = form.querySelector(
      '[type="submit"], button[name="add"], .product-form__submit',
    );
    if (submitBtn) {
      form.insertBefore(clone, submitBtn);
    } else {
      form.appendChild(clone);
    }

    resetCloneFields(clone);
    stripPluginWrappers(clone);
    fixDrawerCarBrand(clone);
    initDrawerSelects(clone);
    initDrawerDependencies(clone);
    attachErrorClearListeners(clone);

    // Variant-based field filtering for cloned drawer
    (function () {
      var pdEl = document.getElementById("pd-data");
      if (!pdEl) return;
      try {
        var pd = JSON.parse(pdEl.textContent);
        var hasVF = (pd.variants || []).some(function (v) {
          return v.allowedFields && v.allowedFields.trim().length > 0;
        });
        var hasItems = (pd.variants || []).some(function (v) {
          return v.includedItems && v.includedItems.length > 0;
        });
        if (hasVF || hasItems) {
          _setupDrawerVariantFilter(form, clone, pd);
        }
      } catch (e) {}
    })();

    var addBtn = form.querySelector(
      '[type="submit"], button[name="add"], .product-form__submit',
    );
    if (!addBtn) return;

    addBtn.addEventListener(
      "click",
      function (e) {
        if (!validateDrawerFields(clone)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        window._cfPendingProps = collectFromElement(clone);
      },
      true,
    );
  }

  function initDrawerDependencies(clone) {
    clone.querySelectorAll("input, select").forEach(function (inp) {
      inp.addEventListener("change", function () {
        var parentWrapper = inp.closest("[data-field-id='combo_set']");
        if (parentWrapper) {
          var selectedCombo = _comboSets.find(function (c) {
            return c.name === inp.value;
          });
          applyAllowedFields(
            selectedCombo ? selectedCombo.allowedIds : null,
            clone,
          );
        }
        checkDrawerDependencies(clone);
      });
    });
    if (_comboSets.length > 1) {
      applyAllowedFields(null, clone);
    }
    checkDrawerDependencies(clone);
  }

  function checkDrawerDependencies(clone) {
    clone.querySelectorAll("[data-depends-on]").forEach(function (wrapper) {
      var rawDependsOn = wrapper.dataset.dependsOn || "";
      var parentIds = rawDependsOn
        .split("|")
        .map(function (s) {
          return s
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_|_$/g, "");
        })
        .filter(function (s) {
          return s.length > 0;
        });
      var showWhen = wrapper.dataset.showWhen;
      var showWhenList = showWhen
        ? showWhen.split("|").map(function (v) {
            return v.trim();
          })
        : [];

      var anyParentFound = false;
      var shouldShow = false;

      for (var i = 0; i < parentIds.length; i++) {
        var parentWrapper = clone.querySelector(
          '[data-field-id="' + parentIds[i] + '"]',
        );
        if (!parentWrapper) continue;

        anyParentFound = true;
        var parentValue = "";
        var checked = parentWrapper.querySelector(
          "input[type='radio']:checked",
        );
        var sel = parentWrapper.querySelector("select");
        parentValue = checked ? checked.value : sel ? sel.value : "";

        if (showWhen === "" || showWhenList.indexOf(parentValue) !== -1) {
          shouldShow = true;
          break;
        }
      }

      if (!anyParentFound) {
        wrapper.style.display = "block";
        return;
      }

      if (shouldShow) {
        wrapper.style.display = "block";
        restoreRequiredInWrapper(wrapper);
      } else {
        wrapper.style.display = "none";
        removeRequiredFromWrapper(wrapper);
      }
    });
  }

  function validateDrawerFields(clone) {
    var valid = true;
    clone.querySelectorAll(".cf-error").forEach(function (e) {
      e.remove();
    });
    clone.querySelectorAll(".cf-field-wrapper").forEach(function (wrapper) {
      if (wrapper.style.display === "none") return;
      if (wrapper.classList.contains("cf-combo-hidden")) return;
      // Validate visible required text/select inputs
      wrapper
        .querySelectorAll(
          'input[required]:not([type="radio"]):not([type="hidden"]), select[required]',
        )
        .forEach(function (inp) {
          if (!inp.value || !inp.value.trim()) {
            var lbl = wrapper.querySelector(".cf-label");
            var name = lbl ? lbl.textContent.replace(" *", "").trim() : "Field";
            wrapper.classList.add("cf-field-error");
            var err = document.createElement("div");
            err.className = "cf-error";
            err.textContent = "Please fill in " + name;
            wrapper.appendChild(err);
            valid = false;
          }
        });
      // Validate ALL radio groups — required flag does not matter
      var radios = wrapper.querySelectorAll('input[type="radio"]');
      if (radios.length > 0) {
        var checked = wrapper.querySelector('input[type="radio"]:checked');
        if (!checked) {
          var lbl = wrapper.querySelector(".cf-label");
          var name = lbl ? lbl.textContent.replace(" *", "").trim() : "Option";
          wrapper.classList.add("cf-field-error");
          var err = document.createElement("div");
          err.className = "cf-error";
          err.textContent = "Please select " + name;
          wrapper.appendChild(err);
          valid = false;
        }
      }
    });
    if (!valid)
      showToast("Please fill all required fields before adding to cart.");
    return valid;
  }

  // ─── DRAWER HELPERS ──────────────────────────────────────────────────────

  /** Remove theme select plugin wrappers and restore native <select> elements. */
  function stripPluginWrappers(container) {
    container.querySelectorAll(".select-wrapper").forEach(function (wrapper) {
      var sel = wrapper.querySelector("select");
      if (sel) {
        sel.classList.remove("hidden", "semantic-select-initialized");
        wrapper.parentNode.insertBefore(sel, wrapper);
        wrapper.remove();
      }
    });
  }

  function initDrawerSelects(clone) {
    clone.querySelectorAll(".cf-select").forEach(function (sel) {
      sel.addEventListener("mousedown", function () {
        sel.classList.toggle("is-open");
      });
      sel.addEventListener("blur", function () {
        sel.classList.remove("is-open");
      });
      sel.addEventListener("change", function () {
        sel.classList.remove("is-open");
      });
    });
  }

  /**
   * Fully rebuild the Car Brand dropdown in the drawer clone.
   * Needed because the theme plugin may have replaced the original <select>.
   */
  function fixDrawerCarBrand(clone) {
    if (!_brands.length) return;

    var brandWrapper = clone.querySelector(
      '.cf-field-wrapper[data-field-id="car_brand"]',
    );
    if (!brandWrapper) return;

    brandWrapper.querySelectorAll("select").forEach(function (el) {
      el.remove();
    });

    var brandLabelEl = brandWrapper.querySelector(".cf-label");
    if (brandLabelEl) {
      brandLabelEl.textContent = "Select " + _brandLabel + " Brand Name *";
    }

    var brandSelect = document.createElement("select");
    brandSelect.className = "cf-select";
    brandSelect.name = "properties[" + _brandLabel + " Brand]";
    brandSelect.required = true;

    var defOpt = document.createElement("option");
    defOpt.value = "";
    defOpt.disabled = true;
    defOpt.selected = true;
    defOpt.textContent = "-- Select Brand --";
    brandSelect.appendChild(defOpt);

    _brands.forEach(function (brand) {
      var opt = document.createElement("option");
      opt.value = brand.name;
      opt.textContent = brand.name;
      brandSelect.appendChild(opt);
    });
    brandWrapper.appendChild(brandSelect);

    var modelWrapper = brandWrapper.nextElementSibling;
    if (!modelWrapper || !modelWrapper.classList.contains("cf-field-wrapper"))
      return;

    var modelLabel = modelWrapper.querySelector(".cf-label");
    modelWrapper.querySelectorAll("select").forEach(function (el) {
      el.remove();
    });
    // Remove any existing preview above brandWrapper
    var existingPreview = brandWrapper.parentNode.querySelector(
      ".cf-car-product-preview",
    );
    if (existingPreview) existingPreview.remove();
    modelWrapper.style.display = "none";

    var modelSelect = document.createElement("select");
    modelSelect.className = "cf-select";
    modelSelect.name = "properties[" + _brandLabel + " Model]";
    modelSelect.required = true;

    var modelDefOpt = document.createElement("option");
    modelDefOpt.value = "";
    modelDefOpt.disabled = true;
    modelDefOpt.selected = true;
    modelDefOpt.textContent = "-- Select Model --";
    modelSelect.appendChild(modelDefOpt);
    modelWrapper.appendChild(modelSelect);

    var productPreview = document.createElement("div");
    productPreview.className = "cf-car-product-preview";
    productPreview.style.display = "none";

    // Insert preview ABOVE brand dropdown
    brandWrapper.parentNode.insertBefore(productPreview, brandWrapper);

    brandSelect.addEventListener("change", function () {
      var selected = _brands.find(function (b) {
        return b.name === brandSelect.value;
      });
      modelSelect.innerHTML =
        '<option value="" disabled selected>-- Select Model --</option>';
      productPreview.style.display = "none";
      productPreview.innerHTML = "";
      if (selected) {
        if (modelLabel)
          modelLabel.textContent = "Select Your " + selected.name + " Model *";
        selected.models.forEach(function (model) {
          var opt = document.createElement("option");
          opt.value = model;
          opt.textContent = model;
          modelSelect.appendChild(opt);
        });
        modelWrapper.style.display = "block";
      } else {
        modelWrapper.style.display = "none";
      }
    });

    modelSelect.addEventListener("change", function () {
      var selectedBrand = _brands.find(function (b) {
        return b.name === brandSelect.value;
      });
      productPreview.innerHTML = "";
      if (!selectedBrand || !modelSelect.value) {
        productPreview.style.display = "none";
        return;
      }
      var card = document.createElement("div");
      card.className = "cf-car-preview-card";
      var selectedModel = selectedBrand
        ? selectedBrand.models.find(function (m) {
            return m.name === modelSelect.value;
          })
        : null;
      if (selectedModel && selectedModel.image) {
        var img = document.createElement("img");
        img.src = selectedModel.image;
        img.alt = selectedBrand.name + " - " + modelSelect.value;
        img.className = "cf-car-preview-img";
        card.appendChild(img);
      }
      var textWrap = document.createElement("div");
      textWrap.className = "cf-car-preview-text";
      var nameEl = document.createElement("div");
      nameEl.className = "cf-car-preview-name";
      nameEl.textContent = selectedBrand.name + " - " + modelSelect.value;
      textWrap.appendChild(nameEl);
      var priceStr = (function () {
        try {
          var pd = JSON.parse(
            (document.getElementById("pd-data") || {}).textContent || "{}",
          );
          if (pd.price) return "\u20B9\u00A0" + (pd.price / 100).toFixed(2);
        } catch (e) {}
        return "";
      })();
      if (priceStr) {
        var priceEl = document.createElement("div");
        priceEl.className = "cf-car-preview-price";
        priceEl.textContent = priceStr;
        textWrap.appendChild(priceEl);
      }
      card.appendChild(textWrap);
      var checkEl = document.createElement("div");
      checkEl.className = "cf-car-preview-check";
      checkEl.innerHTML = "&#10003;";
      card.appendChild(checkEl);
      productPreview.appendChild(card);
      productPreview.style.display = "block";
    });
  }

  /** Reset all fields in a drawer clone to a blank state. */
  function resetCloneFields(clone) {
    clone
      .querySelectorAll(
        'input[type="text"], input[type="email"], input[type="tel"]',
      )
      .forEach(function (inp) {
        inp.value = "";
      });
    clone.querySelectorAll('input[type="radio"]').forEach(function (inp) {
      inp.checked = false;
    });
    clone
      .querySelectorAll('input[type="hidden"][name^="properties["]')
      .forEach(function (inp) {
        inp.value = "";
      });
    clone.querySelectorAll("select").forEach(function (sel) {
      sel.selectedIndex = 0;
    });
    clone.querySelectorAll("[data-depends-on]").forEach(function (w) {
      w.style.display = "none";
    });
  }

  // Watch for section re-renders (variant change triggers Shopify section rendering
  // which replaces DOM including custom-fields-container — re-init when it's re-added).
  var _sectionRenderObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        var reAddedContainer =
          node.id === "custom-fields-container"
            ? node
            : node.querySelector &&
              node.querySelector("#custom-fields-container");
        if (reAddedContainer) {
          // Only re-init for the MAIN product section's container.
          // Skip containers inside recommendations, quickshop, or other sections.
          var parentSection = reAddedContainer.closest(
            "#main-product, .m6pr"
          );
          if (!parentSection) continue; // not in main product section
          if (
            parentSection.className &&
            parentSection.className.indexOf("quickshop") !== -1
          ) continue; // inside quickshop

          // Remove stale tooltips stuck in body (mouseleave never fired on replaced elements)
          document.querySelectorAll(".cf-addon-tooltip").forEach(function (t) {
            t.remove();
          });

          // Re-initialize synchronously before browser paints
          init();
          return;
        }
      }
    }
  });

  /**
   * Prevent quickshop drawer content from being replaced with the
   * main product when a variant select is changed.
   *
   * #quickshop already exists in the DOM (it's a persistent container).
   * The theme populates it with product content via AJAX.  When a
   * variant select changes, the theme fetches a section re-render
   * and replaces the quickshop's article element with the main product.
   *
   * We watch #quickshop's direct children.  When a new product loads
   * (different from main page), we save the product URL.  If the
   * content later changes to the main product, we restore the saved
   * HTML immediately.
   */

  var _qsSavedHTML = null;
  var _qsProductUrl = null;
  var _qsRestoring = false;

  function interceptQuickshopVariantChange() {
    var qs = document.getElementById("quickshop");
    if (!qs) {
      // If #quickshop doesn't exist yet, wait for it
      new MutationObserver(function (muts, obs) {
        var q = document.getElementById("quickshop");
        if (q) {
          obs.disconnect();
          _watchQuickshopContent(q);
        }
      }).observe(document.body, { childList: true, subtree: true });
    } else {
      _watchQuickshopContent(qs);
    }
  }

  /** Init any uninitialized custom-fields containers inside #quickshop.
   * Accepts render areas that are empty OR contain only a skeleton loader
   * (keychain products server-render a skeleton placeholder). */
  function _initQuickshopFields(qs) {
    var containers = qs.querySelectorAll("#custom-fields-container");
    containers.forEach(function (qsCf) {
      var qsForm = qsCf.closest('form[action*="/cart/add"]');
      if (!qsForm) return;
      // Skip installment forms — hide their container
      if (
        qsForm.classList.contains("f8pr-product-form-installment") ||
        (qsForm.getAttribute("id") && qsForm.getAttribute("id").indexOf("installment") !== -1)
      ) {
        qsCf.style.display = "none";
        return;
      }
      var qsRa = qsCf.querySelector("#cf-render-area");
      if (!qsRa || qsRa.dataset.initialized === "true") return;
      // Empty render area OR one that only contains the keychain skeleton
      // placeholder — both mean "not yet initialized, safe to init".
      var hasOnlySkeleton = qsRa.children.length === 1 &&
                            qsRa.children[0].classList.contains("cf-skeleton");
      if (qsRa.children.length === 0 || hasOnlySkeleton) {
        qsForm.dataset.cfDrawerBound = "true";
        _initDrawerOwnContainer(qsForm, qsCf, qsRa);
      }
    });
  }

  function _watchQuickshopContent(qs) {
    // Debounced handler — coalesces rapid mutations during incremental DOM
    // population (e.g. car-brand products with hundreds of metafield divs).
    // Without this, every child insertion triggers full work + an
    // innerHTML serialization, which blocks the main thread and stalls
    // the drawer open animation.
    var _qsDebounceTimer = null;
    function _handleQsMutation() {
      if (_qsDebounceTimer) return;
      _qsDebounceTimer = setTimeout(function () {
        _qsDebounceTimer = null;

        // ── Quickshop field init (ALWAYS runs, even during restore) ──
        _initQuickshopFields(qs);

        // ── Save/restore guard ──
        if (_qsRestoring) return;

        var currentUrl = _getQsProductUrl(qs);
        if (!currentUrl) return;

        var mainUrl = window.location.pathname;

        if (currentUrl !== mainUrl) {
          // Quickshop shows a DIFFERENT product → save it. Only serialize
          // innerHTML when the URL actually changes — not on every mutation.
          if (_qsProductUrl !== currentUrl) {
            _qsProductUrl = currentUrl;
            _qsSavedHTML = qs.innerHTML;
          }
        } else if (
          _qsSavedHTML &&
          _qsProductUrl &&
          currentUrl !== _qsProductUrl
        ) {
          // Theme replaced our saved product with the main page product → restore
          _qsRestoring = true;
          qs.innerHTML = _qsSavedHTML;
          setTimeout(function () {
            _qsRestoring = false;
          }, 300);
        }
      }, 50);
    }

    new MutationObserver(_handleQsMutation).observe(qs, { childList: true, subtree: true });

    // Immediate check — quickshop may already have content when we start watching
    _initQuickshopFields(qs);
  }

  /** Get the product URL from quickshop content using data-product-url */
  function _getQsProductUrl(qs) {
    // Primary: article[data-product-url] (most reliable)
    var article = qs.querySelector("[data-product-url]");
    if (article) return article.dataset.productUrl;
    // Fallback: product link
    var link = qs.querySelector('a[href*="/products/"]');
    if (link) {
      var m = link.getAttribute("href").match(/(\/products\/[^?#\s]+)/);
      if (m) return m[1];
    }
    return null;
  }

  function startAndInit() {
    init();
    interceptQuickshopVariantChange();
    _sectionRenderObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startAndInit);
  } else {
    startAndInit();
  }
})();

/* ══════════════════════════════════════════════════════════════════════════
   HIDDEN IFRAME UPLOAD (no visible page reload)
   ──────────────────────────────────────────────────────────────────────────
   Shopify's /cart/add.js AJAX endpoint doesn't upload files reliably (saves
   fake path). Native form POST does, but it navigates the whole page.

   Trick: point the form's target to a hidden iframe, then submit. Browser
   POSTs multipart to /cart/add inside the iframe — file uploads correctly,
   /uploads/ URL saved as property. Visible page never navigates.

   Once the iframe finishes loading (Shopify redirects to return_to=/cart.js),
   we use the Section Rendering API to refresh the cart drawer + header cart
   count and open the drawer. Custom fields on the product form are cleared
   so the customer can add another one.
   ══════════════════════════════════════════════════════════════════════════ */
(function () {
  var FRAME_NAME = "cf-upload-frame";

  function hasSelectedFile(form) {
    if (!form.action || form.action.indexOf("/cart/add") === -1) return false;
    var files = form.querySelectorAll(
      'input[type="file"][name^="properties["]',
    );
    for (var i = 0; i < files.length; i++) {
      if (files[i].files && files[i].files[0]) return true;
    }
    return false;
  }

  function ensureFrame() {
    var frame = document.getElementById(FRAME_NAME);
    if (frame) return frame;
    frame = document.createElement("iframe");
    frame.id = FRAME_NAME;
    frame.name = FRAME_NAME;
    frame.setAttribute("aria-hidden", "true");
    frame.setAttribute("tabindex", "-1");
    frame.style.cssText =
      "position:absolute !important;left:-9999px !important;top:-9999px !important;width:1px !important;height:1px !important;border:0 !important;visibility:hidden !important;";
    document.body.appendChild(frame);
    return frame;
  }

  function refreshCartAndOpen() {
    return fetch(window.location.pathname + "?sections=side-cart,header")
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        // 1) Cart drawer content
        var cartHtml = data && data["side-cart"];
        if (cartHtml) {
          var tmp = document.createElement("div");
          tmp.innerHTML = cartHtml;
          var freshCart = tmp.querySelector("#cart");
          var currentCart = document.getElementById("cart");
          if (freshCart && currentCart) {
            currentCart.innerHTML = freshCart.innerHTML;
          }
        }

        // 2) Header cart count (desktop + mobile)
        var headerHtml = data && data["header"];
        if (headerHtml) {
          var tmp2 = document.createElement("div");
          tmp2.innerHTML = headerHtml;
          ["#cart-count", "#cart-count--m", "#cart-total"].forEach(function (
            sel,
          ) {
            var fresh = tmp2.querySelector(sel);
            var current = document.querySelector(sel);
            if (fresh && current) current.innerHTML = fresh.innerHTML;
          });
        }

        // 3) Open the drawer via theme's own trigger
        var trigger = document.querySelector('[data-panel="cart"]');
        if (trigger) trigger.click();
      });
  }

  function resetCustomFields(form) {
    var fileInputs = form.querySelectorAll(
      'input[type="file"][name^="properties["]',
    );
    fileInputs.forEach(function (input) {
      input.value = "";
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
    var textInputs = form.querySelectorAll(
      'input[name^="properties["]:not([type="file"]):not([type="radio"]):not([type="checkbox"]):not([type="hidden"]), textarea[name^="properties["]',
    );
    textInputs.forEach(function (input) {
      input.value = "";
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });
  }

  function submitViaHiddenFrame(form, btn, origBtnHTML) {
    var frame = ensureFrame();

    // Save & override form attributes
    var origTarget = form.getAttribute("target");
    var origEnctype = form.getAttribute("enctype");
    form.setAttribute("target", FRAME_NAME);
    form.setAttribute("enctype", "multipart/form-data");

    // return_to → tiny JSON endpoint so iframe loads quickly after redirect
    var returnEl = form.querySelector('input[name="return_to"]');
    var origReturnValue = returnEl ? returnEl.value : null;
    var didAddReturn = !returnEl;
    if (!returnEl) {
      returnEl = document.createElement("input");
      returnEl.type = "hidden";
      returnEl.name = "return_to";
      form.appendChild(returnEl);
    }
    returnEl.value = "/cart.js";

    var done = false;
    var safetyTimer = null;

    function restoreForm() {
      if (origTarget !== null) form.setAttribute("target", origTarget);
      else form.removeAttribute("target");
      if (origEnctype !== null) form.setAttribute("enctype", origEnctype);
      if (didAddReturn) {
        returnEl.remove();
      } else if (origReturnValue !== null) {
        returnEl.value = origReturnValue;
      }
    }

    function finish(success, err) {
      if (done) return;
      done = true;
      if (safetyTimer) clearTimeout(safetyTimer);
      frame.removeEventListener("load", onLoad);

      restoreForm();
      btn.disabled = false;
      if (origBtnHTML !== null) btn.innerHTML = origBtnHTML;

      if (success) {
        refreshCartAndOpen()
          .then(function () {
            resetCustomFields(form);
          })
          .catch(function (e) {
            console.error("[cf-upload] refresh failed:", e);
          });
      } else {
        console.error("[cf-upload] iframe submit failed:", err);
        alert("Upload failed. Please try again.");
      }
    }

    function onLoad() {
      // Ignore the initial about:blank load
      try {
        var url = frame.contentWindow.location.href;
        if (url === "about:blank" || !url) return;
      } catch (e) {
        // Cross-origin shouldn't happen for same-origin submit; treat as done
      }
      finish(true, null);
    }

    frame.addEventListener("load", onLoad);
    safetyTimer = setTimeout(function () {
      finish(false, new Error("timeout"));
    }, 30000);

    form.submit();
  }

  function handleClick(e) {
    var btn =
      e.target.closest &&
      e.target.closest('button[type="submit"], input[type="submit"]');
    if (!btn) return;
    var form = btn.closest && btn.closest("form");
    if (!form) return;
    if (!hasSelectedFile(form)) return;

    if (typeof form.checkValidity === "function" && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    var origHTML = btn.innerHTML;
    btn.disabled = true;
    if (btn.textContent) btn.textContent = "Adding to cart...";

    submitViaHiddenFrame(form, btn, origHTML);
  }

  function init() {
    document.addEventListener("click", handleClick, true);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();


